/**
 * Warranty Table - Add rows (manual, auto when last row complete), row actions, selection
 */
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

(function () {
  'use strict';

  // DOM selectors
  const selectorWarrantyTable = '[data-warranty-table]';
  const selectorTableBody = '.rs-warranty-table__body';
  const selectorTableRow = '.rs-warranty-table__row';
  const selectorAddMoreButton = '[data-warranty-add-more]';
  const selectorDeleteRowButton = '.rs-table-button-delete';
  const selectorDuplicateRowButton = '.rs-table-button-duplicate';
  const selectorSaveBtn = '.rs-warranty-table__save-btn';
  const selectorSubmitBtn = '.rs-warranty-table__submit-btn';

  const skuDebounceMs = 400;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Required field config: name part for input selector, cell class, and whether it depends on "other" being selected */
  const requiredFieldsConfig = [
    {namePart: 'sku', cellClass: 'rs-warranty-table__cell--sku', requiresOther: false},
    {namePart: 'date_code', cellClass: 'rs-warranty-table__cell--date-code', requiresOther: false},
    {namePart: 'date_installed', cellClass: 'rs-warranty-table__cell--date-installed', requiresOther: false},
    {namePart: 'date_removed', cellClass: 'rs-warranty-table__cell--date-removed', requiresOther: false},
    {namePart: 'failure_reason', cellClass: 'rs-warranty-table__cell--failure', requiresOther: false, isSelect: true},
    {namePart: 'failure_reason_other', cellClass: 'rs-warranty-table__cell--failure', requiresOther: true, isSelect: false},
  ];

  /**
   * Initialize the warranty table: row cloning, add more, duplicate, delete, checkboxes.
   * @param {Element} container - The warranty table container element
   */
  function init(container) {
    if (!container) return;

    const tableBody = container.querySelector(selectorTableBody);
    const addMoreBtn = container.querySelector(selectorAddMoreButton);
    const rows = container.querySelectorAll(selectorTableRow);
    const template = rows[0] ? rows[0].cloneNode(true) : null;

    const addMoreCount = parseInt(container.dataset.addMoreCount || '5', 10);
    const maxRows = parseInt(container.dataset.maxRows || '999', 10);
    const addMoreLabel = container.dataset.addMoreLabel || `Add ${addMoreCount} More Entry Fields`;
    const maxReachedTemplate = container.dataset.maxReachedTemplate || 'Maximum Entries Reached ({{ count }}/{{ max }})';

    let rowIdCounter = 0;
    /** @type {Promise<typeof import('./rs-customer-part-number-autocomplete.js')> | null} */
    let customerPartNumberAutocompleteModulePromise = null;

    function loadCustomerPartNumberAutocompleteModule() {
      const moduleUrl = container.dataset.cpnAutocompleteModule;
      if (!moduleUrl) return Promise.resolve(null);
      if (!customerPartNumberAutocompleteModulePromise) {
        customerPartNumberAutocompleteModulePromise = import(moduleUrl);
      }
      return customerPartNumberAutocompleteModulePromise;
    }

    async function initCustomerPartNumberSuggestionsInContainer(root = container) {
      const mod = await loadCustomerPartNumberAutocompleteModule();
      mod?.initCustomerPartNumberAutocompleteInContainer(root);
    }

    async function initCustomerPartNumberSuggestionsInRow(row) {
      if (!row) return;
      await initCustomerPartNumberSuggestionsInContainer(row);
    }

    async function whenCustomerPartNumberSuggestionsSettle() {
      const mod = await loadCustomerPartNumberAutocompleteModule();
      await mod?.whenCustomerPartNumberLookupsSettle?.();
    }

    async function resetCustomerPartNumberSuggestionsInRow(row) {
      if (!row) return;
      const mod = await loadCustomerPartNumberAutocompleteModule();
      mod?.cleanupCustomerPartNumberAutocompleteInRow(row);
      await initCustomerPartNumberSuggestionsInRow(row);
    }
    /** @type {{ draftId: string, items: Array<{id?: string, itemId?: string}> } | null} */
    let loadedDraftData = null;

    function stripWrappingQuotes(value) {
      let text = String(value ?? '').trim();
      if (!text) return '';

      text = text
        .replace(/&quot;/gi, '"')
        .replace(/&#0?39;/g, "'")
        .replace(/&apos;/gi, "'");

      const wrappers = [
        ['"', '"'],
        ["'", "'"],
        ['\u201c', '\u201d'],
        ['\u2018', '\u2019'],
      ];

      let prev;
      do {
        prev = text;
        text = text.trim();

        if (text.startsWith('="') && text.endsWith('"') && text.length > 3) {
          text = text.slice(2, -1).trim();
          continue;
        }

        for (const [open, close] of wrappers) {
          if (text.length >= open.length + close.length && text.startsWith(open) && text.endsWith(close)) {
            text = text.slice(open.length, -close.length).trim();
          }
        }
        text = text.replace(/^["'\u201c\u201d\u2018\u2019]+|["'\u201c\u201d\u2018\u2019]+$/g, '');
      } while (text !== prev);

      return text.trim();
    }

    function normalizeSkuOrCustomerPartNumber(value) {
      return stripWrappingQuotes(value);
    }

    /**
     * Default failure options HTML (from static warranty-failure-reasons-options).
     * Used to restore when SKU is cleared or product not found.
     */
    const defaultFailureOptionsHtml = (() => {
      const firstRow = tableBody?.querySelector(selectorTableRow);
      const failureCell = firstRow?.querySelector('.rs-warranty-table__cell--failure');
      const menu = failureCell?.querySelector('.rs-custom-select__menu');
      return menu ? menu.innerHTML : '';
    })();

    /**
     * Update IDs and related attributes in a cloned row to avoid duplicates.
     * Appends a unique suffix (e.g. -clone-1) to all id, for, aria-controls, aria-labelledby.
     * @param {Element} row - The cloned row element
     */
    function fixClonedIds(row) {
      const suffix = '-clone-' + ++rowIdCounter;
      row.querySelectorAll('[id]').forEach((el) => {
        if (el.id) el.id = el.id + suffix;
      });
      row.querySelectorAll('[for]').forEach((label) => {
        const forId = label.getAttribute('for');
        if (forId) label.setAttribute('for', forId + suffix);
      });
      row.querySelectorAll('[aria-controls]').forEach((el) => {
        const id = el.getAttribute('aria-controls');
        if (id) el.setAttribute('aria-controls', id + suffix);
      });
      row.querySelectorAll('[aria-labelledby]').forEach((el) => {
        const id = el.getAttribute('aria-labelledby');
        if (id) el.setAttribute('aria-labelledby', id + suffix);
      });
    }

    /**
     * Format a Date to YYYY-MM-DD using local calendar fields (avoids UTC off-by-one).
     * @param {Date} d
     * @returns {string}
     */
    function formatDateToIsoLocal(d) {
      if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }

    /**
     * Parse a date string as YYYY-MM-DD or m/d/Y (Flatpickr display) to YYYY-MM-DD.
     * @param {string} str
     * @returns {string}
     */
    function parseDisplayOrIsoDateString(str) {
      if (!str || typeof str !== 'string') return '';
      const t = str.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
      if (typeof flatpickr !== 'undefined') {
        const parsed = flatpickr.parseDate(t, 'm/d/Y');
        if (parsed) return formatDateToIsoLocal(parsed);
      }
      return '';
    }

    /**
     * Convert YYYY-MM-DD to MM/DD/YYYY for preserved display string.
     * @param {string} iso
     * @returns {string}
     */
    function isoDateToMmDdYyyy(iso) {
      if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) return '';
      const [y, m, d] = iso.trim().split('-').map(Number);
      return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
    }

    /**
     * Convert MM/DD/YYYY (or m/d/y) / YYYY-MM-DD to YYYY-MM-DD for native date inputs.
     * @param {string} usOrIso
     * @returns {string}
     */
    function mmDdYyyyToIsoForNativeInput(usOrIso) {
      if (!usOrIso || typeof usOrIso !== 'string') return '';
      const t = usOrIso.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
      if (typeof flatpickr !== 'undefined') {
        const parsed = flatpickr.parseDate(t, 'm/d/Y');
        if (parsed) return formatDateToIsoLocal(parsed);
      }
      return '';
    }

    /**
     * Read the current date from a warranty date field (Flatpickr instance, alt input, or native).
     * Prefer Flatpickr's unnamed alt input — querySelector('…rs-input-type__date') can match the
     * main input first, which is often empty while the alt holds m/d/Y.
     * @param {HTMLInputElement} dateInput
     * @returns {string} MM/DD/YYYY or ''
     */
    function getPreservedDateInputValue(dateInput) {
      const wrapper = dateInput.closest('.rs-input__wrapper');
      const altDisplay = wrapper?.querySelector('input[type="text"].rs-input-type__date:not([name])');
      let iso = '';
      if (altDisplay?.value) {
        iso = parseDisplayOrIsoDateString(altDisplay.value);
      }
      if (!iso && dateInput._flatpickr?.selectedDates?.length) {
        iso = formatDateToIsoLocal(dateInput._flatpickr.selectedDates[0]);
      }
      if (!iso && dateInput.type === 'date' && dateInput.value) {
        iso = parseDisplayOrIsoDateString(dateInput.value);
      }
      if (!iso && dateInput.value) {
        iso = parseDisplayOrIsoDateString(dateInput.value);
      }
      return iso ? isoDateToMmDdYyyy(iso) : '';
    }

    /**
     * Reset cloned date inputs so they can be re-initialized with Flatpickr.
     * Cloned rows inherit Flatpickr's DOM (alt input, flatpickr-input class). This strips
     * that state so initDateInputsInContainer will pick them up.
     * @param {Element} row - The cloned row element
     * @param {boolean} [preserveValues] - When true (e.g. duplicate row), keep the visible date values.
     */
    function resetClonedDateInputs(row, preserveValues) {
      row.querySelectorAll('input[name*="date_installed"], input[name*="date_removed"]').forEach((originalInput) => {
        const preserved = preserveValues ? getPreservedDateInputValue(originalInput) : '';

        if (originalInput._flatpickr) {
          try {
            originalInput._flatpickr.destroy();
          } catch {
            /* ignore */
          }
        }

        originalInput.type = 'date';
        originalInput.classList.remove('flatpickr-input');
        originalInput.removeAttribute('style');
        const wrapper = originalInput.closest('.rs-input__wrapper');
        if (wrapper) {
          wrapper.querySelectorAll('input[type="text"].rs-input-type__date:not([name])').forEach((el) => el.remove());
        }
        originalInput.value = preserveValues ? preserved : '';
      });
    }

    /**
     * After duplicating a row, clear selection UI only (not field values).
     * @param {Element} row
     */
    function resetDuplicateRowSelection(row) {
      row.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
      });
      row.classList.remove('rs-table-body__row--selected');
    }

    /**
     * Copy live .value / checked from sourceRow inputs to targetRow in DOM order.
     * cloneNode(true) does not reliably copy user-edited values (only the value attribute).
     * @param {Element} sourceRow
     * @param {Element} targetRow
     */
    function copyLiveFormValuesFromSourceRow(sourceRow, targetRow) {
      const srcEls = sourceRow.querySelectorAll('input, select, textarea');
      const dstEls = targetRow.querySelectorAll('input, select, textarea');
      const n = Math.min(srcEls.length, dstEls.length);
      for (let i = 0; i < n; i++) {
        const s = srcEls[i];
        const d = dstEls[i];
        if (s.type === 'file') continue;
        if (s.type === 'checkbox' || s.type === 'radio') {
          d.checked = s.checked;
        } else {
          d.value = s.value;
        }
      }
    }

    /**
     * Clear all form values in a row: checkboxes, text/number/date inputs, custom selects.
     * @param {Element} row - The row element to clear
     */
    function clearRow(row) {
      row.querySelectorAll('input[type="checkbox"]').forEach((cb) => (cb.checked = false));
      row.querySelectorAll('input[type="text"], input[type="number"], input[type="date"]').forEach((inp) => {
        inp.value = inp.type === 'number' ? '1' : '';
      });
      row.querySelectorAll('.rs-custom-select').forEach((sel) => {
        const hiddenInput = sel.querySelector('input[type="hidden"]');
        const valueSpan = sel.querySelector('.rs-custom-select__value');
        if (hiddenInput) hiddenInput.value = '';
        if (valueSpan) valueSpan.textContent = sel.querySelector('.rs-custom-select__button')?.dataset?.placeholder || 'Select reason';
        sel.querySelectorAll('.rs-custom-select__option').forEach((opt) => opt.classList.remove('is-selected'));
        delete sel.dataset.initialized;
      });
      row.querySelectorAll('.rs-warranty-table__cell--failure').forEach((cell) => {
        cell.classList.remove('is-other-selected');
        const otherInput = cell.querySelector('input[name*="failure_reason_other"]');
        if (otherInput) otherInput.value = '';
        if (defaultFailureOptionsHtml) {
          const menu = cell.querySelector('.rs-custom-select__menu');
          if (menu) menu.innerHTML = defaultFailureOptionsHtml;
          const sel = cell.querySelector('.rs-custom-select');
          if (sel && typeof window.initCustomSelect === 'function') {
            delete sel.dataset.initialized;
            window.initCustomSelect(cell.querySelector('.rs-custom-select__wrapper'));
          }
        }
      });
      row.removeAttribute('data-material-group-3');
      row.classList.remove('rs-table-body__row--selected');
    }

    /**
     * Restore default failure options in a row's failure select.
     * @param {Element} row - The row element
     * @param {boolean} reinit - If true, re-initialize the custom select
     */
    function restoreDefaultFailureOptions(row, reinit) {
      const failureCell = row?.querySelector('.rs-warranty-table__cell--failure');
      const menu = failureCell?.querySelector('.rs-custom-select__menu');
      const wrapper = failureCell?.querySelector('.rs-custom-select__wrapper');
      if (menu && defaultFailureOptionsHtml) menu.innerHTML = defaultFailureOptionsHtml;
      if (reinit && typeof window.initCustomSelect === 'function' && wrapper) {
        const selectEl = wrapper.querySelector('.rs-custom-select');
        if (selectEl) delete selectEl.dataset.initialized;
        window.initCustomSelect(wrapper);
      }
    }

    /**
     * Replace failure select options with API failure reasons.
     * If the row already had a selected option that still appears in the API results
     * (by id, code, or label), re-select it and restore any "other" text.
     * @param {Element} row - The row element
     * @param {Array} failureReasons - Array of { id, code, reason, requiresOtherReason }
     */
    function populateFailureSelectWithApiOptions(row, failureReasons) {
      const failureCell = row?.querySelector('.rs-warranty-table__cell--failure');
      const menu = failureCell?.querySelector('.rs-custom-select__menu');
      const wrapper = failureCell?.querySelector('.rs-custom-select__wrapper');
      const valueSpan = wrapper?.querySelector('.rs-custom-select__value');
      const hiddenInput = wrapper?.querySelector('input[name*="failure_reason"]:not([name*="other"])');
      const otherInput = failureCell?.querySelector('.rs-warranty-table__failure-other-input, input[name*="failure_reason_other"]');
      const placeholder = wrapper?.querySelector('.rs-custom-select__button')?.dataset?.placeholder || 'Select reason';
      if (!menu || !failureReasons?.length) return;

      const selectedOpt = wrapper?.querySelector('.rs-custom-select__option.is-selected');
      const prevValue = (hiddenInput?.value || selectedOpt?.dataset?.value || '').trim();
      const prevCode = (selectedOpt?.dataset?.code || '').trim().toLowerCase();
      const prevLabel =
        valueSpan && !valueSpan.classList.contains('rs-custom-select__value--placeholder')
          ? (valueSpan.textContent || '').trim()
          : (selectedOpt?.getAttribute('aria-label') || selectedOpt?.querySelector('.rs-custom-select__option-label')?.textContent || '').trim();
      const prevOther = otherInput?.value || '';

      const fragment = document.createDocumentFragment();
      failureReasons.forEach((fr) => {
        const li = document.createElement('li');
        li.className = 'rs-custom-select__option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.setAttribute('data-value', String(fr.id));
        if (fr.code) li.dataset.code = String(fr.code);
        li.setAttribute('aria-selected', 'false');
        li.setAttribute('aria-label', fr.reason);
        if (fr.requiresOtherReason) li.dataset.requiresOtherReason = 'true';
        const span = document.createElement('span');
        span.className = 'rs-custom-select__option-label';
        span.textContent = fr.reason;
        li.appendChild(span);
        fragment.appendChild(li);
      });
      menu.innerHTML = '';
      menu.appendChild(fragment);

      if (valueSpan) {
        valueSpan.textContent = placeholder;
        valueSpan.classList.add('rs-custom-select__value--placeholder');
      }
      if (hiddenInput) hiddenInput.value = '';
      failureCell?.classList.remove('is-other-selected');
      if (otherInput) otherInput.value = '';

      if (typeof window.initCustomSelect === 'function') {
        const selectEl = wrapper?.querySelector('.rs-custom-select');
        if (selectEl) delete selectEl.dataset.initialized;
        window.initCustomSelect(wrapper);
      }

      if (!prevValue && !prevCode && !prevLabel) return;

      const options = Array.from(wrapper?.querySelectorAll('.rs-custom-select__option') || []);
      const getOptVal = (o) => (o.dataset.value || o.getAttribute('data-value') || '').trim();
      const getOptCode = (o) => (o.dataset.code || o.getAttribute('data-code') || '').trim().toLowerCase();
      const getOptLabel = (o) =>
        (o.getAttribute('aria-label') || o.querySelector('.rs-custom-select__option-label')?.textContent || '').trim();

      const match =
        options.find((o) => prevValue && getOptVal(o) === prevValue) ||
        options.find((o) => prevCode && getOptCode(o) === prevCode) ||
        options.find((o) => prevLabel && getOptLabel(o).toLowerCase() === prevLabel.toLowerCase());

      if (!match) return;

      options.forEach((o) => {
        const isMatch = o === match;
        o.classList.toggle('is-selected', isMatch);
        o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      if (valueSpan) {
        valueSpan.textContent = getOptLabel(match) || getOptVal(match);
        valueSpan.classList.remove('rs-custom-select__value--placeholder');
      }
      if (hiddenInput) hiddenInput.value = getOptVal(match);
      if (otherInput && prevOther) otherInput.value = prevOther;
      syncFailureOtherInput(failureCell);
    }

    /**
     * Get context from cookie.
     * @returns {Object|null}
     */
    function getContextFromCookie() {
      try {
        const ck = {};
        (document.cookie || '').split(';').forEach((s) => {
          const i = s.indexOf('=');
          if (i >= 0) ck[decodeURIComponent(s.slice(0, i).trim())] = decodeURIComponent(s.slice(i + 1).trim());
        });
        return ck.context ? JSON.parse(ck.context) : null;
      } catch {
        return null;
      }
    }

    /**
     * Build payload for check-visibility API.
     * @param {string[]} skus - Array of SKUs
     * @returns {Object|null} Payload or null
     */
    function buildCheckVisibilityPayload(skus) {
      const ctx = getContextFromCookie();
      if (!ctx || !skus?.length) return null;
      const sa = ctx.sales_area;
      const salesAreaIds = Array.isArray(sa) ? sa.map((s) => s?.fields?.external_id) : [sa?.fields?.external_id];
      const validIds = salesAreaIds.filter(Boolean);
      if (!validIds.length) return null;
      return {
        sku: skus.map((s) => String(s).trim()),
        sales_area: validIds,
        company: ctx.company_id || null,
        state_code: ctx.state_code || null,
      };
    }

    /**
     * Check SKU visibility via check-visibility API.
     * @param {string[]} skus - Array of SKUs
     * @returns {Promise<Object>} Map of sku -> { visible: boolean }
     */
    async function checkSkuVisibility(skus) {
      if (!skus?.length) return {};
      let token = window.myJWT;
      if (!token || typeof token !== 'string') {
        await new Promise((r) => setTimeout(r, 500));
        token = window.myJWT;
      }
      if (!token || typeof token !== 'string') return {};

      const payload = buildCheckVisibilityPayload(skus);
      if (!payload) return {};

      try {
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/products/check-visibility`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) return {};
        return await res.json();
      } catch (e) {
        console.warn('Warranty table: check-visibility failed', e);
        return {};
      }
    }

    /**
     * Build payload for check-eligibility API.
     * @param {string} sku - SKU or part number
     * @returns {Object} Payload for check-eligibility
     */
    function buildCheckEligibilityPayload(sku) {
      const ctx = getContextFromCookie();
      const sa = ctx?.sales_area;
      const saRaw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
      const locationId = ctx?.location_id?.replace?.('gid://shopify/CompanyLocation/', '') || null;
      return {
        company_id: ctx?.company_id || null,
        company_location_gid: ctx?.company_location_gid || null,
        customer_gid: document.body?.dataset?.customerId ? 'gid://shopify/Customer/' + document.body.dataset.customerId : null,
        location_id: locationId,
        state_code: ctx?.state_code || null,
        sales_organization: saRaw?.sales_org_id ?? ctx?.sales_org_id ?? null,
        distribution_channel: saRaw?.distribution_channel_id ?? ctx?.distribution_channel_id ?? null,
        division: saRaw?.division_id ?? ctx?.division_id ?? null,
        products: [{sku: sku.trim(), quantity: 1}],
      };
    }

    /**
     * Fetch variant GID by SKU using check-eligibility endpoint.
     * @param {string} sku - SKU or part number to search
     * @returns {Promise<string|null>} Variant GID or null
     */
    async function fetchVariantGidBySku(sku) {
      if (!sku?.trim()) return null;

      let token = window.myJWT;
      if (!token || typeof token !== 'string') {
        await new Promise((r) => setTimeout(r, 500));
        token = window.myJWT;
      }
      if (!token || typeof token !== 'string') return null;

      try {
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/products/check-eligibility`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify(buildCheckEligibilityPayload(sku)),
        });
        if (!res.ok) return null;
        const data = await res.json();
        const variantId = data?.results?.[0]?.id;
        return variantId || null;
      } catch (e) {
        console.warn('Warranty table: check-eligibility failed', e);
        return null;
      }
    }

    /**
     * Fetch failure reasons from API for given product GIDs.
     * @param {string[]} productIds - Array of product GIDs
     * @returns {Promise<Object>} Map of productId -> { failureReasons: [...] }
     */
    async function fetchFailureReasons(productIds) {
      if (!productIds?.length) return {};
      const token = window.myJWT;
      if (!token || typeof token !== 'string') return {};
      try {
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns/failure-reasons`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({productIds}),
        });
        if (!res.ok) return {};
        return await res.json();
      } catch (e) {
        console.warn('Warranty table: failure reasons fetch failed', e);
        return {};
      }
    }

    /**
     * Handle SKU input: check-visibility for field_scrap, fetch product, then failure reasons, and populate failure select.
     * @param {Element} row - The row containing the SKU input
     * @param {string} sku - The SKU value
     * @param {boolean} [applyErrors] - Mark the row when the SKU is not found / not returnable. Only
     *   set once the user has left the field, so a half-typed SKU is not flagged.
     */
    async function handleSkuChange(row, sku, applyErrors = false) {
      sku = normalizeSkuOrCustomerPartNumber(sku);
      if (container.dataset.populatingFromDraft) return;
      const failureCell = row?.querySelector('.rs-warranty-table__cell--failure');
      if (!failureCell) return;

      const returnValueEl = row?.querySelector('.rs-warranty-return__value');

      if (!sku?.trim()) {
        row.removeAttribute('data-material-group-3');
        if (returnValueEl) returnValueEl.textContent = '';
        restoreDefaultFailureOptions(row, true);
        const wrapper = failureCell.querySelector('.rs-custom-select__wrapper');
        const valueSpan = wrapper?.querySelector('.rs-custom-select__value');
        const hiddenInput = wrapper?.querySelector('input[name*="failure_reason"]');
        const placeholder = wrapper?.querySelector('.rs-custom-select__button')?.dataset?.placeholder || 'Select reason';
        if (valueSpan) {
          valueSpan.textContent = placeholder;
          valueSpan.classList.add('rs-custom-select__value--placeholder');
        }
        if (hiddenInput) hiddenInput.value = '';
        wrapper?.querySelectorAll('.rs-custom-select__option').forEach((o) => o.classList.remove('is-selected'));
        failureCell.classList.remove('is-other-selected');
        const otherInput = failureCell.querySelector('input[name*="failure_reason_other"]');
        if (otherInput) otherInput.value = '';
        return;
      }

      const [visibilityResults, variantGid] = await Promise.all([checkSkuVisibility([sku]), fetchVariantGidBySku(sku)]);
      const visibilityEntry = visibilityResults[sku];
      const fieldScrap = visibilityEntry?.additionalData?.salesData?.field_scrap;
      const returnTypeLabel = fieldScrap === '1' ? 'Scrap' : 'Return';
      if (returnValueEl) returnValueEl.textContent = returnTypeLabel;
      setRowMaterialGroup3(row, visibilityEntry);
      // Flag the row from the same check the upload path uses, so a typed SKU that is not found
      // or not returnable surfaces here instead of waiting for save/submit.
      if (applyErrors) {
        applySkuVisibilityToRow(row, visibilityEntry);
        validateWarrantyExpiry(row);
        updateSubmitButtonState();
        updateSaveButtonState();
      }
      if (!variantGid) {
        restoreDefaultFailureOptions(row, true);
        return;
      }

      const data = await fetchFailureReasons([variantGid]);
      const entry = data[variantGid];
      const failureReasons = entry?.failureReasons;
      if (!failureReasons?.length) {
        restoreDefaultFailureOptions(row, true);
        return;
      }

      populateFailureSelectWithApiOptions(row, failureReasons);
    }

    /**
     * Restrict date code inputs to digits only (YYWW format, max 4 characters),
     * or the literal text "NDC" (case-insensitive, normalized to uppercase).
     */
    function initDateCodeInput() {
      const dateCodeInputSelector = '.rs-warranty-table__cell--date-code input[name*="date_code"]';
      const maxLength = 4;

      function sanitizeDateCodeValue(value) {
        const upper = String(value || '').toUpperCase().replace(/\s/g, '');
        if (upper.startsWith('N')) {
          let i = 0;
          while (i < NDC_VALUE.length && upper[i] === NDC_VALUE[i]) i++;
          return upper.slice(0, i);
        }
        return upper.replace(/\D/g, '').slice(0, maxLength);
      }

      container.addEventListener('keydown', (e) => {
        const input = e.target;
        if (!input.matches?.(dateCodeInputSelector)) return;
        if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
        const allowedNav = new Set(['Tab', 'Escape', 'Enter', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);
        if (allowedNav.has(e?.key)) return;
        if (e?.key?.length === 1) {
          const start = input.selectionStart ?? 0;
          const end = input.selectionEnd ?? 0;
          const raw = input.value.slice(0, start) + e.key + input.value.slice(end);
          if (sanitizeDateCodeValue(raw) !== raw.toUpperCase()) e.preventDefault();
          return;
        }
        e.preventDefault();
      });

      container.addEventListener('input', (e) => {
        const input = e.target;
        if (!input.matches?.(dateCodeInputSelector)) return;
        const cleaned = sanitizeDateCodeValue(input.value);
        if (input.value !== cleaned) input.value = cleaned;
      });

      container.addEventListener('paste', (e) => {
        const input = e.target;
        if (!input.matches?.(dateCodeInputSelector)) return;
        e.preventDefault();
        const text = e.clipboardData?.getData('text') ?? '';
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const next = sanitizeDateCodeValue(input.value.slice(0, start) + text + input.value.slice(end));
        input.value = next;
        const caret = Math.min(start + sanitizeDateCodeValue(text).length, next.length);
        try {
          input.setSelectionRange(caret, caret);
        } catch (_) {}
        input.dispatchEvent(new Event('input', {bubbles: true}));
      });
    }

    /**
     * Initialize debounced SKU input listener on blur/input: check-visibility (field_scrap) and failure reasons.
     */
    function initSkuFailureFetch() {
      const skuInputSelector = '.rs-warranty-table__cell--sku input';
      const debounceMap = new Map();

      function resolveSkuFromInput(input) {
        const typed = normalizeSkuOrCustomerPartNumber(input?.value);
        const customerPartNumberSku = input?.nextElementSibling?.querySelector('.customer-part-number-suggestion');
        if (customerPartNumberSku && normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.partNumber) === typed) {
          return normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.sku);
        }
        if (input.getAttribute('data-selected-sku')) return normalizeSkuOrCustomerPartNumber(input.getAttribute('data-selected-sku'));
        return typed;
      }

      function debouncedHandle(row, input, applyErrors) {
        const key = row;
        if (debounceMap.has(key)) clearTimeout(debounceMap.get(key));
        debounceMap.set(
          key,
          setTimeout(async () => {
            debounceMap.delete(key);
            // The keystroke queued a customer part number lookup, and the SKU is read from the
            // suggestion it renders. Wait for it so a typed part number is not sent as a SKU.
            await whenCustomerPartNumberSuggestionsSettle();
            handleSkuChange(row, resolveSkuFromInput(input), applyErrors);
          }, skuDebounceMs),
        );
      }

      container.addEventListener('input', (e) => {
        const input = e.target;
        if (!input.matches(skuInputSelector)) return;
        const cleaned = normalizeSkuOrCustomerPartNumber(input.value);
        if (cleaned !== input.value) input.value = cleaned;
        const row = input.closest(selectorTableRow);
        if (!row) return;
        debouncedHandle(row, input, false);
      });

      // Capture: blur does not bubble, so the listener has to see it on the way down.
      container.addEventListener(
        'blur',
        (e) => {
          const input = e.target;
          if (!input.matches?.(skuInputSelector)) return;
          const cleaned = normalizeSkuOrCustomerPartNumber(input.value);
          if (cleaned !== input.value) input.value = cleaned;
          const row = input.closest(selectorTableRow);
          if (!row) return;
          debouncedHandle(row, input, true);
        },
        true,
      );
    }

    /**
     * Re-initialize custom select components in a cloned row.
     * Clears the initialized flag so initCustomSelect runs again.
     * @param {Element} row - The cloned row element
     */
    function reinitCustomSelects(row) {
      row.querySelectorAll('.rs-custom-select__wrapper').forEach((wrapper) => {
        const selectEl = wrapper.querySelector('.rs-custom-select');
        if (selectEl && typeof window.initCustomSelect === 'function') {
          delete selectEl.dataset.initialized;
          window.initCustomSelect(wrapper);
        }
      });
    }

    const dateInputSelector = 'input[type="date"].rs-input-type__date:not(.flatpickr-input)';

    /**
     * Initialize Flatpickr on date inputs within a container.
     * Only targets the original date inputs (type="date"), not alt inputs, and skips already-initialized ones.
     * @param {Element} container - Element to search for date inputs (e.g. row or tableBody)
     */
    function initDateInputsInContainer(container) {
      if (!container || typeof flatpickr === 'undefined') return;
      if (typeof window.initDateInputs === 'function') {
        window.initDateInputs(container);
        return;
      }
      container.querySelectorAll(dateInputSelector).forEach((input) => {
        if (input._flatpickr) return;
        try {
          const placeholder = input.getAttribute('placeholder') || 'mm/dd/yyyy';
          flatpickr(input, {
            dateFormat: 'm/d/Y',
            altInput: true,
            altFormat: 'm/d/Y',
            altInputClass: 'rs-input rs-input-type__date text-medium text-primary',
            allowInput: true,
            disableMobile: true,
            maxDate: 'today',
            parseDate: (datestr, format) => flatpickr.parseDate(datestr, format),
            onReady: function (selectedDates, dateStr, instance) {
              if (instance.altInput) instance.altInput.placeholder = placeholder;
            },
          });
        } catch (e) {
          console.warn('Flatpickr init failed for date input:', e);
        }
      });
    }

    /**
     * Watch for new rows added to the table body and initialize date inputs in them.
     * Uses MutationObserver as a fallback so date pickers work even if the add flow misses init.
     */
    function observeNewRows() {
      if (!tableBody || typeof MutationObserver === 'undefined') return;
      const observer = new MutationObserver((mutations) => {
        const rowsToInit = [];
        for (const mutation of mutations) {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                if (node.matches?.(selectorTableRow)) rowsToInit.push(node);
                else node.querySelectorAll?.(selectorTableRow)?.forEach((r) => rowsToInit.push(r));
              }
            });
          }
        }
        if (rowsToInit.length) {
          setTimeout(() => {
            rowsToInit.forEach((row) => initDateInputsInContainer(row));
            rowsToInit.forEach((row) => initCustomerPartNumberSuggestionsInRow(row));
          }, 0);
        }
      });
      observer.observe(tableBody, {childList: true, subtree: true});
    }

    /**
     * Update the Add More button label and disabled state based on current row count.
     */
    function updateAddMoreButton() {
      if (!addMoreBtn || !tableBody) return;
      const rowCount = tableBody.querySelectorAll(selectorTableRow).length;
      const span = addMoreBtn.querySelector('span');
      if (span) {
        if (rowCount >= maxRows) {
          span.textContent = maxReachedTemplate.replace(/\{\{\s*count\s*\}\}/g, String(rowCount)).replace(/\{\{\s*max\s*\}\}/g, String(maxRows));
          addMoreBtn.disabled = true;
        } else {
          span.textContent = addMoreLabel;
          addMoreBtn.disabled = false;
        }
      }
    }

    /**
     * Remove a row. If it was the last row, add a fresh empty row from the template.
     * @param {Element} row - The row to delete
     */
    function deleteRow(row) {
      row.remove();
      if (!tableBody.querySelector(selectorTableRow) && template) {
        const newRow = template.cloneNode(true);
        newRow.classList.add('rs-warranty-table__row--clone');
        clearRow(newRow);
        resetClonedDateInputs(newRow);
        reinitCustomSelects(newRow);
        tableBody.appendChild(newRow);
        initDateInputsInContainer(newRow);
        initCustomerPartNumberSuggestionsInRow(newRow);
      }
      updateAddMoreButton();
    }

    /**
     * Duplicate a row with the same field values, inserted immediately after the source row.
     * @param {Element} row - The row to duplicate
     */
    function duplicateRow(row) {
      const currentRows = tableBody.querySelectorAll(selectorTableRow).length;
      if (currentRows >= maxRows) return;
      const clone = row.cloneNode(true);
      clone.classList.add('rs-warranty-table__row--clone');
      copyLiveFormValuesFromSourceRow(row, clone);
      fixClonedIds(clone);
      resetDuplicateRowSelection(clone);
      resetClonedDateInputs(clone, true);
      reinitCustomSelects(clone);
      const failureCell = clone.querySelector('.rs-warranty-table__cell--failure');
      if (failureCell) syncFailureOtherInput(failureCell);
      row.insertAdjacentElement('afterend', clone);
      initDateInputsInContainer(clone);
      resetCustomerPartNumberSuggestionsInRow(clone);
      if (typeof window.RsActions !== 'undefined' && window.RsActions.close) {
        window.RsActions.close(clone.querySelector('.rs-actions'));
      }
      updateAddMoreButton();
    }

     /**
     * Update the header checkbox based on the current body row selection state.
     */
    function updateMasterCheckboxState() {
      const masterInput = container.querySelector(
        '.rs-table-header .rs-checkbox__content input[type="checkbox"]'
      );

      if (!masterInput) return;

      const rowCheckboxes = Array.from(
        tableBody.querySelectorAll('.rs-checkbox__content input[type="checkbox"]')
      );

      const selectedCount = rowCheckboxes.filter((cb) => cb.checked).length;
      const totalCount = rowCheckboxes.length;

      const allSelected = totalCount > 0 && selectedCount === totalCount;
      const partiallySelected = selectedCount > 0 && selectedCount < totalCount;

      masterInput.checked = allSelected;
      masterInput.indeterminate = partiallySelected;

      if (partiallySelected) {
        masterInput.setAttribute('aria-checked', 'mixed');
      } else {
        masterInput.setAttribute('aria-checked', allSelected ? 'true' : 'false');
      }
    }

    /**
     * Wire up the "select all" checkbox in the header to toggle all row checkboxes.
     */
    /**
 * Wire up the "select all" checkbox in the header to toggle all row checkboxes.
 */
    function initMasterCheckbox() {
      const masterCheckbox = container.querySelector('.rs-table-checkbox__select-all');
      const masterInput = container.querySelector(
        '.rs-table-header .rs-checkbox__content input[type="checkbox"]'
      );

      if (!masterCheckbox || !masterInput) return;

      masterCheckbox.addEventListener('click', (e) => {
        e.preventDefault();

        const next = !masterInput.checked;

        masterInput.checked = next;
        masterInput.indeterminate = false;
        masterInput.setAttribute('aria-checked', next ? 'true' : 'false');

        tableBody
          .querySelectorAll('.rs-checkbox__content input[type="checkbox"]')
          .forEach((cb) => {
            cb.checked = next;

            const r = cb.closest(selectorTableRow);
            if (r) {
              r.classList.toggle('rs-table-body__row--selected', next);
            }
          });

        if (masterInput.checked) {
          container
            ?.querySelector('.rs-warranty-multiple-selection')
            ?.classList.remove('rs-hidden');

          updateSelectionBar();
        } else {
          container
            ?.querySelector('.rs-warranty-multiple-selection')
            ?.classList.add('rs-hidden');

          updateSelectionBar();
        }
      });

      masterInput.addEventListener('change', () => {
        const checked = masterInput.checked;

        masterInput.indeterminate = false;
        masterInput.setAttribute('aria-checked', checked ? 'true' : 'false');

        tableBody
          .querySelectorAll('.rs-checkbox__content input[type="checkbox"]')
          .forEach((cb) => {
            cb.checked = checked;

            const r = cb.closest(selectorTableRow);
            if (r) {
              r.classList.toggle('rs-table-body__row--selected', checked);
            }
          });
      });
    }

    /**
     * Toggle the "Other" failure reason input visibility based on selected value.
     * Shows when value is 'other' (static options) or selected option has data-requires-other-reason.
     * @param {Element} failureCell - The failure reason cell element
     */
    function syncFailureOtherInput(failureCell) {
      const wrapper = failureCell.querySelector('.rs-custom-select__wrapper');
      const hiddenInput = wrapper?.querySelector('input[name*="failure_reason"]');
      const value = hiddenInput?.value || '';
      let isOther = value === 'other';
      if (!isOther && value) {
        const selectedOpt = wrapper?.querySelector('.rs-custom-select__option.is-selected');
        isOther = selectedOpt?.dataset.requiresOtherReason === 'true';
      }
      failureCell.classList.toggle('is-other-selected', isOther);
    }

    /**
     * Initialize failure "Other" input visibility and change listener.
     */
    function initFailureOtherInputs() {
      container.querySelectorAll('.rs-warranty-table__cell--failure').forEach(syncFailureOtherInput);

      container.addEventListener('change', (e) => {
        const wrapper = e.target.closest?.('.rs-custom-select__wrapper');
        if (!wrapper) return;
        const failureCell = wrapper.closest('.rs-warranty-table__cell--failure');
        if (!failureCell) return;
        syncFailureOtherInput(failureCell);
      });
    }

    /**
     * Float failure-reason dropdowns over the overflow-x:auto scroll wrapper via position:fixed.
     * Mirrors rs-private-label.js _initApnPnDropdownFloat for the same overflow clipping issue.
     */
    function initFailureSelectDropdownFloat() {
      const scrollWrapper = container.querySelector('.rs-warranty-table__scroll-wrapper');
      if (!scrollWrapper || typeof MutationObserver === 'undefined') return;

      const desktopMq = window.matchMedia('(min-width: 750px)');

      /** @type {MutationObserver | null} */
      let selectObserver = null;

      function shouldFloat() {
        return desktopMq.matches;
      }

      function positionMenu(select) {
        const menu = select.querySelector('.rs-custom-select__menu');
        const btn = select.querySelector('.rs-custom-select__button');
        if (!menu || !btn) return;
        const rect = btn.getBoundingClientRect();
        menu.style.position = 'fixed';
        menu.style.top = (rect.bottom + 4) + 'px';
        menu.style.left = rect.left + 'px';
        menu.style.width = 'auto';
        menu.style.minWidth = Math.max(rect.width, 220) + 'px';
        menu.style.zIndex = '9999';
      }

      function resetMenu(select) {
        const menu = select.querySelector('.rs-custom-select__menu');
        if (!menu) return;
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.width = '';
        menu.style.minWidth = '';
        menu.style.zIndex = '';
      }

      function handleSelectOpenState(select) {
        if (select.classList.contains('open') && shouldFloat()) {
          positionMenu(select);
        } else {
          resetMenu(select);
        }
      }

      function bindSelect(select) {
        if (select.dataset.failureFloatBound === 'true') return;
        select.dataset.failureFloatBound = 'true';
        selectObserver?.observe(select, {attributes: true, attributeFilter: ['class']});
      }

      function bindAllSelects() {
        container.querySelectorAll('.rs-warranty-table__cell--failure .rs-custom-select').forEach(bindSelect);
      }

      selectObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          handleSelectOpenState(/** @type {HTMLElement} */ (mutation.target));
        }
      });

      bindAllSelects();

      function updateOpenMenus() {
        if (!shouldFloat()) {
          container.querySelectorAll('.rs-warranty-table__cell--failure .rs-custom-select.open').forEach(resetMenu);
          return;
        }
        container.querySelectorAll('.rs-warranty-table__cell--failure .rs-custom-select.open').forEach(positionMenu);
      }

      function closeOpenMenus() {
        container.querySelectorAll('.rs-warranty-table__cell--failure .rs-custom-select.open').forEach((select) => {
          const btn = select.querySelector('.rs-custom-select__button');
          if (!btn) return;
          btn.click();
          btn.blur();
        });
      }

      scrollWrapper.addEventListener('scroll', updateOpenMenus, {passive: true});
      document.addEventListener('scroll', (e) => {
        const target = e.target;
        if (target === document || target === document.documentElement || target === document.body) {
          closeOpenMenus();
        }
      }, {passive: true, capture: true});
      window.addEventListener('resize', updateOpenMenus, {passive: true});
      desktopMq.addEventListener('change', updateOpenMenus);

      if (tableBody) {
        const rowObserver = new MutationObserver(bindAllSelects);
        rowObserver.observe(tableBody, {childList: true, subtree: true});
      }
    }

    /**
     * Sync row selection state when individual row checkboxes change or are clicked.
     */
    function initRowCheckboxes() {
      container.addEventListener('change', (e) => {
      const input = e.target;
        if (!input.matches('.rs-warranty-table__body .rs-checkbox__content input[type="checkbox"]')) {
          return;
        }
        const row = input.closest(selectorTableRow);
        if (row) {
          row.classList.toggle('rs-table-body__row--selected', input.checked);
        }
        updateMasterCheckboxState();
      });

      container.addEventListener('click', (e) => {
        const hit = e.target.closest('.rs-table-body__checkbox-row');
        if (!hit || !tableBody.contains(hit)) return;
        e.preventDefault();
        const input = hit.querySelector('.rs-checkbox__content input[type="checkbox"]');
        if (input) {
          input.checked = !input.checked;
          const row = hit.closest(selectorTableRow);
          if (row) {
            row.classList.toggle('rs-table-body__row--selected', input.checked);
          }
          updateMasterCheckboxState();
        }
      });
    }

    /**
     * Show selection bar above table when rows are selected. Wire dropdown (Select all, Unselect all) and Remove.
     */
    const selectionBar = container.querySelector('[data-warranty-multiple-selection]');
    const countSpan = selectionBar?.querySelector('[data-warranty-selected-count]');
    const selectButton = selectionBar?.querySelector('.rs-cart-multiple__select-button');
    const optionsEl = selectionBar?.querySelector('.rs-cart-multiple__options');
    const removeBtn = selectionBar?.querySelector('.rs-warranty-multiple__remove-btn');
    const masterInput = container.querySelector('.rs-table-header .rs-checkbox__content input[type="checkbox"]');

    function initWarrantyMultipleSelection() {
      container.addEventListener('change', (e) => {
        if (!e.target.matches?.('input[type="checkbox"]')) return;
        updateSelectionBar();
      });

      selectButton?.addEventListener('click', () => {
        optionsEl?.classList.toggle('rs-hidden');
        selectButton?.setAttribute('aria-expanded', optionsEl?.classList.contains('rs-hidden') ? 'false' : 'true');
      });

      optionsEl?.querySelectorAll('.rs-cart-multiple__option').forEach((opt) => {
        opt.addEventListener('click', (e) => {
          const val = e.target.value;
          if (val === 'all') {
            getRowCheckboxes().forEach((cb) => {
              cb.checked = true;

              const r = cb.closest(selectorTableRow);

              if (r) {
                r.classList.add('rs-table-body__row--selected');
              }
            });

            updateMasterCheckboxState();
          } else if (val === 'unselect') {
            getRowCheckboxes().forEach((cb) => {
              cb.checked = false;

              const r = cb.closest(selectorTableRow);

              if (r) {
                r.classList.remove('rs-table-body__row--selected');
              }
            });

            updateMasterCheckboxState();
          }
          optionsEl?.classList.add('rs-hidden');
          selectButton?.setAttribute('aria-expanded', 'false');
          updateSelectionBar();
        });
      });

      removeBtn?.addEventListener('click', () => {
        getRowCheckboxes().forEach((cb) => {
          if (cb.checked) {
            const row = cb.closest(selectorTableRow);
            if (row) row.remove();
          }
        });
        if (masterInput) masterInput.checked = false;
        updateSelectionBar();
        updateAddMoreButton();
      });

      document.addEventListener('click', (e) => {
        if (!selectionBar?.contains(e.target)) {
          optionsEl?.classList.add('rs-hidden');
          selectButton?.setAttribute('aria-expanded', 'false');
        }
      });
    }

    function getRowCheckboxes() {
      return tableBody ? tableBody.querySelectorAll('.rs-checkbox__content input[type="checkbox"]') : [];
    }

    function getSelectedCount() {
      return Array.from(getRowCheckboxes()).filter((cb) => cb.checked).length;
    }

    function updateSelectionBar() {
      if (!selectionBar || !countSpan) return;
      const count = getSelectedCount();
      const suffix = count === 1 ? selectionBar.dataset.rowSelected || 'row selected' : selectionBar.dataset.rowsSelected || 'rows selected';
      countSpan.textContent = `${count} ${suffix}`;
      selectionBar.classList.toggle('rs-hidden', count === 0);
    }

    /**
     * Handle click events for delete and duplicate buttons in row action menus.
     */
    function initRowActions() {
      container.addEventListener('click', (e) => {
        const deleteBtn = e.target.closest(selectorDeleteRowButton);
        if (deleteBtn) {
          const row = deleteBtn.closest(selectorTableRow);
          if (row) deleteRow(row);
          return;
        }
        const duplicateBtn = e.target.closest(selectorDuplicateRowButton);
        if (duplicateBtn) {
          const row = duplicateBtn.closest(selectorTableRow);
          if (row) duplicateRow(row);
          return;
        }
        const pencilBtn = e.target.closest('.rs-warranty-table__mob-label-edit');
        if (pencilBtn) {
          e.preventDefault();
          const cell = pencilBtn.closest('.rs-warranty-table__cell');
          const input = cell?.querySelector('input:not([type="hidden"])');
          if (input) input.focus();
        }
      });
    }

    /**
     * Get the value of a field in a row. For custom selects, uses the hidden input.
     * @param {Element} row - The row element
     * @param {Object} config - Field config from requiredFieldsConfig
     * @returns {string} Trimmed value
     */
    function getRowFieldValue(row, config) {
      const cell = row.querySelector(`.${config.cellClass}`);
      if (!cell) return '';
      if (config.isSelect) {
        const hiddenInput = cell.querySelector('input[name*="failure_reason"]:not([name*="other"])');
        return hiddenInput ? (hiddenInput.value || '').trim() : '';
      }
      const input = cell.querySelector(`input[name*="${config.namePart}"]`);
      if (input) {
        const typed = config.namePart === 'sku' ? normalizeSkuOrCustomerPartNumber(input.value) : (input.value || '').trim();
        const customerPartNumberSku = input?.nextElementSibling?.querySelector('.customer-part-number-suggestion');
        if (customerPartNumberSku && normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.partNumber) === typed) {
          return normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.sku);
        } else if (input.getAttribute('data-selected-sku')) {
          return normalizeSkuOrCustomerPartNumber(input.getAttribute('data-selected-sku'));
        }
        return typed;
      }
      return '';
    }

    /**
     * Check if a row has any meaningful data (is "filled").
     * @param {Element} row - The row element
     * @returns {boolean}
     */
    function isRowFilled(row) {
      const hasValue = (namePart) => {
        const input = row.querySelector(`input[name*="${namePart}"]`);
        return input && (input.value || '').trim() !== '';
      };
      return hasValue('reference_number') || hasValue('sku') || hasValue('date_code') || hasValue('date_installed') || hasValue('date_removed') || hasValue('failure_reason') || hasValue('failure_reason_other');
    }

    /**
     * Get ISO week number of year (1-53) for a given date.
     * @param {Date} date - Date to get week for
     * @returns {number}
     */
    function getISOWeek(date) {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    }

    const NDC_VALUE = 'NDC';

    /**
     * "NDC" is always an accepted date code value, regardless of sales area or product.
     * @param {string} raw
     * @returns {boolean}
     */
    function isNdcDateCode(raw) {
      return String(raw || '').trim().toUpperCase() === NDC_VALUE;
    }

    /**
     * Validate date code format: YYWW (week 01-52, year 2 digits).
     * Week cannot be > 52. Year cannot be > current year.
     * When year equals current year, week must be < current week.
     * @param {Element} row - The row element
     * @returns {boolean} True if valid or empty
     */
    function validateDateCode(row) {
      const raw = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-code', namePart: 'date_code', isSelect: false});
      if (!raw) return true;

      const wrapper = row.querySelector('input[name*="date_code"]')?.closest('.rs-input__content');
      if (!wrapper) return true;
      if (isNdcDateCode(raw)) {
        wrapper.classList.remove('rs-error');
        return true;
      }
      const value = raw.replace(/\D/g, '');

      const match = value.length === 4 ? value.match(/^(\d{2})(\d{2})$/) : null;
      if (!match) {
        wrapper.classList.add('rs-error');
        row.classList.add('rs-warranty-table__row--error');
        const msgSpan = wrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = 'Invalid code';
        return false;
      }

      const year2 = parseInt(match[1], 10);
      const week = parseInt(match[2], 10);
      if (week < 1 || week > 52) {
        wrapper.classList.add('rs-error');
        row.classList.add('rs-warranty-table__row--error');
        const msgSpan = wrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = 'Invalid code';
        return false;
      }

      const fullYear = 2000 + year2;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentWeek = getISOWeek(now);

      if (fullYear > currentYear) {
        wrapper.classList.add('rs-error');
        row.classList.add('rs-warranty-table__row--error');
        const msgSpan = wrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = 'Invalid code';
        return false;
      }

      if (fullYear === currentYear && week >= currentWeek) {
        wrapper.classList.add('rs-error');
        row.classList.add('rs-warranty-table__row--error');
        const msgSpan = wrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = 'Invalid code';
        return false;
      }

      wrapper.classList.remove('rs-error');
      return true;
    }

    /**
     * YYWW date code validity (same rules as validateDateCode when raw is non-empty). No DOM.
     * @param {string} raw
     * @returns {boolean}
     */
    function isDateCodeValueValid(raw) {
      if (!raw || !(raw || '').trim()) return false;
      if (isNdcDateCode(raw)) return true;
      const value = raw.replace(/\D/g, '');
      const match = value.length === 4 ? value.match(/^(\d{2})(\d{2})$/) : null;
      if (!match) return false;
      const year2 = parseInt(match[1], 10);
      const week = parseInt(match[2], 10);
      if (week < 1 || week > 52) return false;
      const fullYear = 2000 + year2;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentWeek = getISOWeek(now);
      if (fullYear > currentYear) return false;
      if (fullYear === currentYear && week >= currentWeek) return false;
      return true;
    }

    /**
     * Parse date string (supports m/d/Y and YYYY-MM-DD) to Date or null.
     * @param {string} str - Date string
     * @returns {Date|null}
     */
    function parseDate(str) {
      if (!str || typeof str !== 'string') return null;
      const trimmed = str.trim();
      if (!trimmed) return null;
      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : d;
    }

    /**
     * Validate date installed is before date removed. Add rs-error to both if invalid.
     * Only applies when both dates have values.
     * @param {Element} row - The row element
     * @returns {boolean} True if valid or not applicable (either date empty)
     */
    function validateDateOrder(row) {
      const installedWrapper = row.querySelector('input[name*="date_installed"]')?.closest('.rs-input__content');
      const removedWrapper = row.querySelector('input[name*="date_removed"]')?.closest('.rs-input__content');
      const installedVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-installed', namePart: 'date_installed', isSelect: false});
      const removedVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-removed', namePart: 'date_removed', isSelect: false});

      if (!installedVal || !removedVal) return true;

      const installedDate = parseDate(installedVal);
      const removedDate = parseDate(removedVal);
      if (!installedDate || !removedDate) return true;

      if (installedDate >= removedDate) {
        const dateOrderErrorInstalled = 'Must be before removed date';
        const dateOrderErrorRemoved = 'Must be after install date';
        if (installedWrapper) {
          installedWrapper.classList.add('rs-error');
          const msgSpan = installedWrapper.querySelector('.rs-error__message span');
          if (msgSpan) msgSpan.textContent = dateOrderErrorInstalled;
        }
        if (removedWrapper) {
          removedWrapper.classList.add('rs-error');
          const msgSpan = removedWrapper.querySelector('.rs-error__message span');
          if (msgSpan) msgSpan.textContent = dateOrderErrorRemoved;
        }
        row.classList.add('rs-warranty-table__row--error');
        return false;
      }
      if (installedWrapper) installedWrapper.classList.remove('rs-error');
      if (removedWrapper) removedWrapper.classList.remove('rs-error');
      return true;
    }

    /**
     * Installed date strictly before removed date; both must have values. No DOM (for auto-add row).
     * @param {Element} row
     * @returns {boolean}
     */
    function isDateOrderValidForCompleteRow(row) {
      const installedVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-installed', namePart: 'date_installed', isSelect: false});
      const removedVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-removed', namePart: 'date_removed', isSelect: false});
      if (!installedVal || !removedVal) return false;
      const installedDate = parseDate(installedVal);
      const removedDate = parseDate(removedVal);
      if (!installedDate || !removedDate) return false;
      return installedDate < removedDate;
    }

    /**
     * All required fields filled and date rules satisfied (no DOM mutation).
     * @param {Element} row
     * @returns {boolean}
     */
    function isRowCompleteForAutoAdd(row) {
      if (!isRowFilled(row)) return false;
      const failureCell = row.querySelector('.rs-warranty-table__cell--failure');
      const isOtherSelected = failureCell?.classList.contains('is-other-selected') ?? false;

      for (const config of requiredFieldsConfig) {
        if (config.requiresOther && !isOtherSelected) continue;
        const value = getRowFieldValue(row, config);
        if (value === '') return false;
      }

      const dateCodeRaw = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-code', namePart: 'date_code', isSelect: false});
      if (!isDateCodeValueValid(dateCodeRaw)) return false;

      if (!isDateOrderValidForCompleteRow(row)) return false;
      if (isWarrantyExpiredForRow(row)) return false;

      return true;
    }

    /**
     * salesData row from a check-visibility entry (object or array).
     * @param {Object} entry
     * @returns {Object|null}
     */
    function getSalesDataFromVisibility(entry) {
      const ad = entry?.additionalData;
      if (!ad || typeof ad !== 'object') return null;
      const sd = ad.salesData ?? ad.sales_data;
      if (!sd) return null;
      if (Array.isArray(sd)) {
        const ctx = getContextFromCookie();
        const sa = ctx?.sales_area;
        const saRaw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
        const salesAreaId = saRaw?.external_id || sa?.external_id;
        const match = salesAreaId ? sd.find((row) => row?.sales_area === salesAreaId) : null;
        return match || sd[0] || null;
      }
      return sd;
    }

    /**
     * Warranty duration string from check-visibility (e.g. 36M, 5Y).
     * @param {Object} entry
     * @returns {string}
     */
    function getMaterialGroup3FromVisibility(entry) {
      const sd = getSalesDataFromVisibility(entry);
      const raw = sd?.material_group_3 ?? sd?.materialGroup3;
      if (raw == null) return '';
      return String(raw).trim();
    }

    /**
     * Persist material_group_3 from check-visibility on the row for expiry checks.
     * @param {Element} row
     * @param {Object} entry
     */
    function setRowMaterialGroup3(row, entry) {
      if (!row) return;
      const value = getMaterialGroup3FromVisibility(entry);
      if (value) {
        row.setAttribute('data-material-group-3', value);
      } else {
        row.removeAttribute('data-material-group-3');
      }
    }

    /**
     * Parse material_group_3 into amount + unit. Trailing M = months, Y = years; default M.
     * @param {string} raw
     * @returns {{amount: number, unit: 'M'|'Y'}|null}
     */
    function parseWarrantyDuration(raw) {
      const match = String(raw || '').trim().match(/^(\d+)\s*([MY])?$/i);
      if (!match) return null;
      const amount = parseInt(match[1], 10);
      if (!Number.isFinite(amount)) return null;
      return {amount, unit: (match[2] || 'M').toUpperCase()};
    }

    /**
     * Local calendar date from a warranty date field (ISO or m/d/Y).
     * @param {string} str
     * @returns {Date|null}
     */
    function parseWarrantyDateLocal(str) {
      const iso = parseDisplayOrIsoDateString(str);
      if (iso) {
        const [y, m, d] = iso.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return isNaN(date.getTime()) ? null : date;
      }
      return parseDate(str);
    }

    /**
     * True when date_installed + material_group_3 is before today.
     * @param {string} dateInstalled
     * @param {string} materialGroup3
     * @returns {boolean}
     */
    function isWarrantyExpired(dateInstalled, materialGroup3) {
      const duration = parseWarrantyDuration(materialGroup3);
      if (!duration) return false;
      const installedDate = parseWarrantyDateLocal(dateInstalled);
      if (!installedDate) return false;
      const expiry = new Date(installedDate.getFullYear(), installedDate.getMonth(), installedDate.getDate());
      if (duration.unit === 'Y') {
        expiry.setFullYear(expiry.getFullYear() + duration.amount);
      } else {
        expiry.setMonth(expiry.getMonth() + duration.amount);
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      return expiry < today;
    }

    /**
     * Expired when the row has date_installed and data-material-group-3.
     * @param {Element} row
     * @returns {boolean}
     */
    function isWarrantyExpiredForRow(row) {
      const installedVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-installed', namePart: 'date_installed', isSelect: false});
      const materialGroup3 = (row.getAttribute('data-material-group-3') || '').trim();
      if (!installedVal || !materialGroup3) return false;
      return isWarrantyExpired(installedVal, materialGroup3);
    }

    /**
     * Flag date installed when the warranty period has already ended.
     * @param {Element} row
     * @returns {boolean} True if valid or not applicable
     */
    function validateWarrantyExpiry(row) {
      if (!isWarrantyExpiredForRow(row)) return true;
      const installedWrapper = row.querySelector('input[name*="date_installed"]')?.closest('.rs-input__content');
      if (installedWrapper) {
        installedWrapper.classList.add('rs-error');
        const msgSpan = installedWrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = container.dataset.warrantyExpired || 'Warranty expired';
      }
      row.classList.add('rs-warranty-table__row--error');
      return false;
    }

    /**
     * Apply a check-visibility result to one row's SKU field: rs-error when the SKU is not found
     * or not returnable, cleared otherwise.
     * @param {Element} row - The row to mark
     * @param {Object} entry - check-visibility result for that row's SKU (may be undefined)
     * @returns {boolean} True if the SKU is valid
     */
    function applySkuVisibilityToRow(row, entry) {
      setRowMaterialGroup3(row, entry);
      const wrapper = row.querySelector('.rs-warranty-table__cell--sku .rs-input__content');
      if (!wrapper) return true;
      const isVisible = entry && entry.visible === true;
      const isNotReturnable = entry?.additionalData?.salesData?.returnable === false;
      const hasError = !isVisible || isNotReturnable;
      if (hasError) {
        wrapper.classList.add('rs-error');
        row.classList.add('rs-warranty-table__row--error');
        const msgSpan = wrapper.querySelector('.rs-error__message span');
        if (msgSpan) msgSpan.textContent = isNotReturnable ? 'SKU is not returnable' : 'SKU not found';
      } else {
        wrapper.classList.remove('rs-error');
      }
      return !hasError;
    }

    /**
     * Validate SKUs via check-visibility API. Adds rs-error when SKU not found or when additionalData.salesData.returnable is false.
     * @param {Element} tableContainer - The warranty table container
     * @returns {Promise<boolean>} True if all SKUs valid
     */
    async function validateSkus(tableContainer) {
      const tableBody = tableContainer.querySelector(selectorTableBody);
      const rows = tableBody ? tableBody.querySelectorAll(selectorTableRow) : tableContainer.querySelectorAll(selectorTableRow);
      const skuRows = [];
      const skus = [];
      rows.forEach((row) => {
        if (!isRowFilled(row)) return;
        let skuVal = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--sku', namePart: 'sku', isSelect: false});
        const customerPartNumberSku = row.querySelector('.rs-warranty-table__cell--sku .customer-part-number-suggestion');
        if (customerPartNumberSku && normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.partNumber) === normalizeSkuOrCustomerPartNumber(skuVal)) {
          skuVal = normalizeSkuOrCustomerPartNumber(customerPartNumberSku.dataset.sku);
        } else if (row.querySelector('[data-selected-sku]')) {
          skuVal = normalizeSkuOrCustomerPartNumber(row.querySelector('[data-selected-sku]').dataset.sku);
        } else {
          skuVal = normalizeSkuOrCustomerPartNumber(skuVal);
        }
        if (!skuVal) return;
        skuRows.push({row, sku: skuVal});
        if (!skus.includes(skuVal)) skus.push(skuVal);
      });
      if (!skus.length) return true;

      const results = await checkSkuVisibility(skus);
      let isValid = true;
      skuRows.forEach(({row, sku}) => {
        if (!applySkuVisibilityToRow(row, results[sku])) isValid = false;
        if (!validateWarrantyExpiry(row)) isValid = false;
      });
      return isValid;
    }

    /**
     * Validate all filled rows: add rs-error to required field wrappers when empty.
     * Remove rs-error from valid fields. Returns true if all filled rows are valid.
     * @param {Element} tableContainer - The warranty table container
     * @returns {boolean} True if valid
     */
    function validateRows(tableContainer) {
      const tableBody = tableContainer.querySelector(selectorTableBody);
      const rows = tableBody ? tableBody.querySelectorAll(selectorTableRow) : tableContainer.querySelectorAll(selectorTableRow);
      let isValid = true;

      rows.forEach((row, index) => {
        if (!isRowFilled(row) && !(index === 0)) return;

        const failureCell = row.querySelector('.rs-warranty-table__cell--failure');
        const isOtherSelected = failureCell?.classList.contains('is-other-selected');

        const ctx = getContextFromCookie();
        const sa = ctx.sales_area;
        const saRaw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
        const distributionChannel = parseInt(saRaw?.distribution_channel_id ?? ctx?.distribution_channel_id ?? '');

        requiredFieldsConfig.forEach((config) => {
          if (config.requiresOther && !isOtherSelected) return;

          const value = getRowFieldValue(row, config);
          let wrapper;
          if (config.isSelect) {
            wrapper = failureCell?.querySelector('.rs-custom-select__wrapper');
          } else {
            const cell = row.querySelector(`.${config.cellClass}`);
            const fieldEl = cell?.querySelector(`input[name*="${config.namePart}"]`);
            wrapper = fieldEl?.closest('.rs-input__content');
          }

          if (!wrapper) return;

          if (distributionChannel === 11 && config.namePart !== 'date_installed' && config.namePart !== 'date_removed') {
            if (value === '') {
              wrapper.classList.add('rs-error');
              row.classList.add('rs-warranty-table__row--error');
              isValid = false;
              const msgSpan = wrapper.querySelector('.rs-error__message span');
              if (msgSpan) msgSpan.textContent = 'This field is required';
            }
          } else if (value === '' && distributionChannel !== 11) {
            wrapper.classList.add('rs-error');
            row.classList.add('rs-warranty-table__row--error');
            isValid = false;
            if (config.namePart === 'date_installed' || config.namePart === 'date_removed') {
              const msgSpan = wrapper.querySelector('.rs-error__message span');
              if (msgSpan) msgSpan.textContent = 'This field is required';
            }
          } else {
            wrapper.classList.remove('rs-error');
          }
        });

        if (!validateDateCode(row)) isValid = false;
        if (!validateDateOrder(row)) isValid = false;
        if (!validateWarrantyExpiry(row)) isValid = false;

        if (!row.querySelector('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error')) {
          row.classList.remove('rs-warranty-table__row--error');
        }
      });

      return isValid;
    }

    /**
     * Format date string to YYYY-MM-DD. Accepts m/d/Y or YYYY-MM-DD.
     * @param {string} str - Date string
     * @returns {string} YYYY-MM-DD or empty
     */
    function formatDateForApi(str) {
      if (!str || typeof str !== 'string') return '';
      const trimmed = str.trim();
      if (!trimmed) return '';
      const d = new Date(trimmed);
      if (isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }

    /**
     * Build payload for warranty-returns API from form data and context.
     * @param {Element} tableContainer - The warranty table container
     * @param {boolean} draft - True for save for later
     * @returns {Promise<Object|null>} Payload or null
     */
    async function buildWarrantyReturnsPayload(tableContainer, draft) {
      const ctx = getContextFromCookie();
      if (!ctx) return null;

      const sa = ctx.sales_area;
      const saRaw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
      const storeName = (window.Shopify?.shop || 'dev-customer-resideo').replace('.myshopify.com', '') || 'dev-customer-resideo';
      const customerId = document.body?.dataset?.customerId || '';
      const soldTo = document.body.dataset.soldTo;
      const shipTo = document.body.dataset.shipTo;
      const salesArea = saRaw?.external_id || sa?.external_id || '';
      const distributionChannel = parseInt(saRaw?.distribution_channel_id ?? ctx?.distribution_channel_id ?? '');
      const division = saRaw?.division_id ?? ctx?.division_id ?? '';
      const salesOrg = saRaw?.sales_org_id ?? ctx?.sales_org_id ?? '';

      const returnTypeChannel11Select = document.querySelector('.rs-warranty-return-info__input.rs-custom-select__wrapper')
      const returnTypeChannel11Selected = returnTypeChannel11Select?.querySelector('.rs-custom-select__option.is-selected')?.getAttribute('aria-label');

      const section = tableContainer.closest('.rs-warranty-initiate-return');
      const returnInfo = section?.querySelector('.rs-warranty-return-info');

      const rows = tableContainer.querySelectorAll(selectorTableRow);
      const items = [];

      for (const row of rows) {
        if (!isRowFilled(row)) continue;

        const referenceNumber = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--ref', namePart: 'reference_number', isSelect: false});
        const partNumber = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--sku', namePart: 'sku', isSelect: false});
        const quantityVal = row.querySelector('input[name*="quantity"]')?.value;
        const quantity = parseInt(quantityVal, 10) || 1;
        const dateCode = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-code', namePart: 'date_code', isSelect: false});
        const dateInstalled = formatDateForApi(getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-installed', namePart: 'date_installed', isSelect: false}));
        const dateRemoved = formatDateForApi(getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-removed', namePart: 'date_removed', isSelect: false}));
        const failureReasonId = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--failure', namePart: 'failure_reason', isSelect: true});
        const failureReasonText = row.querySelector('.rs-warranty-table__cell--failure .rs-custom-select__option.is-selected')?.getAttribute('aria-label');
        const failureReasonOtherText = row.querySelector('.rs-warranty-table__cell--failure textarea')?.value?.trim() || '';

        const variantGid = partNumber ? await fetchVariantGidBySku(partNumber) : null;

        const selectedOpt = row.querySelector('.rs-warranty-table__cell--failure .rs-custom-select__option.is-selected');
        const failureCode = selectedOpt?.dataset?.code || '';

        const returnValEl = row.querySelector('.rs-warranty-return__value');
        const rowReturnLabel = (returnValEl?.textContent || '').trim();
        
        let returnTypeObject = null;
        if (distributionChannel !== 11) {
          const itemReturnTypes = await getReturnType(false, distributionChannel);
          const itemReturnType = itemReturnTypes.find((item) => item.title === rowReturnLabel);
          returnTypeObject = {
            "gadgetId": itemReturnType.id,
            "type": itemReturnType.title,
            "documentType": itemReturnType.documentType,
            "code": itemReturnType.code
          }
        }

        const item = {
          referenceNumber: referenceNumber || '',
          partNumber,
          gid: variantGid || undefined,
          quantity,
          dateCode: dateCode || undefined,
          dateInstalled: dateInstalled || undefined,
          dateRemoved: dateRemoved || undefined,
          failureReason: failureReasonId ? {gadgetId: failureReasonId, code: failureCode, reason: failureReasonText} : undefined,
          failureReasonOtherText: failureReasonOtherText || undefined,
          ...(returnTypeObject ? {returnType: returnTypeObject} : {}),
        };
        items.push(item);
      }

      if (!items.length) return null;

      const customerReference = returnInfo?.querySelector('input[name*="customer_reference"]')?.value?.trim() || undefined;
      const emailRecipients = getEmailRecipients(returnInfo);

      let returnTypeHeaderObject = null;
      // If channel is 11 get the return type for the header
      if (distributionChannel === 11) {
        const returnTypeHeaders = await getReturnType(true, distributionChannel);
        const returnTypeHeader = returnTypeHeaders?.find((item) => {
          return item.title === returnTypeChannel11Selected;
        });
        if (returnTypeHeader) {
          returnTypeHeaderObject = {
            "gadgetId": returnTypeHeader.id,
            "type": returnTypeChannel11Selected,
            "documentType": returnTypeHeader.documentType,
            "code": returnTypeHeader.code
          }
        }
      }

      return {
        draft,
        storeName,
        customerId,
        ...(returnTypeHeaderObject !== null && { returnType: returnTypeHeaderObject }),
        soldTo: String(soldTo).padStart(6, '0'),
        shipTo: String(shipTo).padStart(6, '0'),
        salesArea,
        distributionChannel: String(distributionChannel),
        division: String(division),
        salesOrg: String(salesOrg),
        customerReference: customerReference || undefined,
        emailRecipients,
        items,
      };
    }

    /**
     * @param {boolean} isHeader
     * @param {any} distributionChannel
     * @returns {Promise<any[]>}
     */
    async function getReturnType(isHeader, distributionChannel) {
      const token = window.myJWT;
      if (!token || typeof token !== 'string') return [];

      try {
        const res = await fetch(
          `${getCustomerAppBaseUrl()}/api/warranty-returns/return-types?header=${isHeader}&distributionChannel=${distributionChannel}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : data?.returnTypes || data?.data || [];
      } catch (error) {
        console.error('Error fetching return types:', error);
        return [];
      }
    }

    /**
     * Replace return-type select options with API return types and reset selection.
     * @param {Element} wrapper - `.rs-custom-select__wrapper.rs-warranty-return-info__input`
     * @param {Array} returnTypes - Array of { id, title, description, code, documentType, type }
     */
    function populateReturnTypeSelect(wrapper, returnTypes) {
      const menu = wrapper?.querySelector('.rs-custom-select__menu');
      const valueSpan = wrapper?.querySelector('.rs-custom-select__value');
      const hiddenInput = wrapper?.querySelector('input[name*="return_type"]');
      const placeholder = wrapper?.querySelector('.rs-custom-select__button')?.dataset?.placeholder || 'Select type';
      if (!menu || !returnTypes?.length) return;

      const fragment = document.createDocumentFragment();
      returnTypes.forEach((rt) => {
        const title = rt.title || '';
        if (!title) return;
        const li = document.createElement('li');
        li.className = 'rs-custom-select__option';
        li.setAttribute('role', 'option');
        li.setAttribute('tabindex', '0');
        li.setAttribute('data-value', String(rt.code || rt.id || ''));
        li.setAttribute('aria-selected', 'false');
        li.setAttribute('aria-label', title);
        if (rt.id) li.dataset.id = String(rt.id);
        if (rt.code) li.dataset.code = String(rt.code);
        if (rt.documentType) li.dataset.documentType = String(rt.documentType);
        if (rt.description) li.dataset.description = String(rt.description);
        if (rt.type) li.dataset.type = String(rt.type);
        const span = document.createElement('span');
        span.className = 'rs-custom-select__option-label';
        span.textContent = title;
        li.appendChild(span);
        fragment.appendChild(li);
      });
      menu.innerHTML = '';
      menu.appendChild(fragment);

      if (valueSpan) {
        valueSpan.textContent = placeholder;
        valueSpan.classList.add('rs-custom-select__value--placeholder');
      }
      if (hiddenInput) hiddenInput.value = '';

      const tooltipText = wrapper.querySelector('.rs-tooltip-text');
      if (tooltipText) {
        tooltipText.innerHTML = returnTypes
          .filter((rt) => rt.title)
          .map((rt) => {
            const safeTitle = String(rt.title)
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;');
            const safeDesc = String(rt.description || '')
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;');
            return `<p><strong>${safeTitle}</strong> ${safeDesc}</p>`;
          })
          .join('');
      }

      if (typeof window.initCustomSelect === 'function') {
        const selectEl = wrapper.querySelector('.rs-custom-select');
        if (selectEl) delete selectEl.dataset.initialized;
        window.initCustomSelect(wrapper);
      }
    }

    /**
     * Update quantity inputs based on Channel ID and selected Return Type.
    */

    function updateQuantityInputsForReturnType() {
      const section = container.closest('.rs-warranty-initiate-return');

      const returnTypeWrapper = section?.querySelector(
        '.rs-custom-select__wrapper.rs-warranty-return-info__input'
      );

      const selectedOption = returnTypeWrapper?.querySelector(
        '.rs-custom-select__option.is-selected'
      );

      const returnType = (
        selectedOption?.getAttribute('aria-label') ||
        selectedOption?.querySelector('.rs-custom-select__option-label')?.textContent ||
        ''
      ).trim().toLowerCase();

      const ctx = getContextFromCookie();
      const salesArea = ctx?.sales_area;

      const salesAreaData = Array.isArray(salesArea)
        ? salesArea[0]?.fields || salesArea[0]
        : salesArea?.fields || salesArea || {};

      const channelId = parseInt(
        salesAreaData?.distribution_channel_id ??
          ctx?.distribution_channel_id ??
          container.dataset?.channelId ??
          '',
        10
      );

      const canEditQuantity =
        channelId === 11 &&
        ['inline reject', 'inspection reject'].includes(returnType);

      container
        .querySelectorAll('input[name="return[rows][][quantity]"]')
        .forEach((input) => {
          if (canEditQuantity) {
            // Allow editing for the two permitted Return Types.
            input.disabled = false;
            input.classList.remove('text-disabled');

            const wrapper = input.closest('.rs-quantity-input-wrapper');
            wrapper?.classList.remove('rs-quantity-input-disabled');
          } else {
            // Reset the quantity whenever editing is not permitted.
            if (input.value !== '1') {
              input.value = '1';

              // Notify any existing listeners of the reset.
              input.dispatchEvent(new Event('input', { bubbles: true }));
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // Disable the input and restore its disabled styling.
            input.disabled = true;
            input.classList.add('text-disabled');

            const wrapper = input.closest('.rs-quantity-input-wrapper');
            wrapper?.classList.add('rs-quantity-input-disabled');
          }
        });
   }

    /**
     * Initialize quantity input behavior for Return Type changes.
     */
    function initQuantityInputsForReturnType() {
      const section = container.closest('.rs-warranty-initiate-return');

      const returnTypeWrapper = section?.querySelector(
        '.rs-custom-select__wrapper.rs-warranty-return-info__input'
      );

      if (!returnTypeWrapper) return;

      // Update after an option is clicked.
      returnTypeWrapper.addEventListener('click', (event) => {
        if (event.target.closest('.rs-custom-select__option')) {
          setTimeout(updateQuantityInputsForReturnType, 0);
        }
      });

      // Update when the custom dropdown changes its selected option.
      const menu = returnTypeWrapper.querySelector('.rs-custom-select__menu');

      if (menu && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
          updateQuantityInputsForReturnType();
        });

        observer.observe(menu, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class', 'aria-selected'],
        });
      }

      // Set the initial state.
      updateQuantityInputsForReturnType();
    }

    /**
     * Fetch header return types for channel 11 and populate the return-info select.
     * @returns {Promise<void>}
     */

    async function initReturnTypeSelect() {
      const channelId = container.dataset?.channelId;
      if (channelId !== '11' && channelId !== 11) return;

      const section = container.closest('.rs-warranty-initiate-return');
      const wrapper = section?.querySelector('.rs-custom-select__wrapper.rs-warranty-return-info__input');
      if (!wrapper) return;

      const waitForJWT = (maxMs = 5000) =>
        new Promise((resolve) => {
          if (window.myJWT) return resolve(true);
          const onReady = () => {
            cleanup();
            resolve(true);
          };
          const timeout = setTimeout(() => {
            cleanup();
            resolve(!!window.myJWT);
          }, maxMs);
          function cleanup() {
            clearTimeout(timeout);
            window.removeEventListener('rs:jwt-ready', onReady);
          }
          window.addEventListener('rs:jwt-ready', onReady);
        });

      if (!(await waitForJWT())) return;

      const ctx = getContextFromCookie();
      const sa = ctx?.sales_area;
      const saRaw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
      const distributionChannel = parseInt(saRaw?.distribution_channel_id ?? ctx?.distribution_channel_id ?? channelId, 10);
      if (!distributionChannel) return;

      const returnTypes = await getReturnType(true, distributionChannel);
      if (returnTypes?.length) populateReturnTypeSelect(wrapper, returnTypes);
    }

    /**
     * Build update payload for warranty-returns update API (when editing a draft).
     * @param {Element} tableContainer - The warranty table container
     * @returns {Promise<Object|null>} Update payload or null
     */
    async function buildWarrantyReturnsUpdatePayload(tableContainer) {
      const section = tableContainer.closest('.rs-warranty-initiate-return');
      const returnInfo = section?.querySelector('.rs-warranty-return-info');
      const customerReference = returnInfo?.querySelector('input[name*="customer_reference"]')?.value?.trim() || undefined;
      const emailRecipients = getEmailRecipients(returnInfo);

      const rows = tableContainer.querySelectorAll(selectorTableRow);
      const channelId = tableContainer.dataset?.channelId;
      const showReturnType = channelId !== '11' && channelId !== 11;

      const itemsToCreate = [];
      const itemsToUpdate = [];
      const currentItemIds = new Set();

      for (const row of rows) {
        if (!isRowFilled(row)) continue;

        const itemId = row.getAttribute('data-item-id');
        const referenceNumber = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--ref', namePart: 'reference_number', isSelect: false});
        const partNumber = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--sku', namePart: 'sku', isSelect: false});
        const quantityVal = row.querySelector('input[name*="quantity"]')?.value;
        const quantity = parseInt(quantityVal, 10) || 1;
        const dateCode = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-code', namePart: 'date_code', isSelect: false});
        const dateInstalled = formatDateForApi(getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-installed', namePart: 'date_installed', isSelect: false}));
        const dateRemoved = formatDateForApi(getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--date-removed', namePart: 'date_removed', isSelect: false}));
        const failureReasonId = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--failure', namePart: 'failure_reason', isSelect: true});
        const failureReasonOtherText = getRowFieldValue(row, {cellClass: 'rs-warranty-table__cell--failure', namePart: 'failure_reason_other', isSelect: false}) || row.querySelector('.rs-warranty-table__cell--failure .rs-custom-select__option.is-selected')?.dataset?.reason || '';
        const selectedOpt = row.querySelector('.rs-warranty-table__cell--failure .rs-custom-select__option.is-selected');
        const failureCode = selectedOpt?.dataset?.code || failureReasonId || '';

        const variantGid = partNumber ? await fetchVariantGidBySku(partNumber) : null;

        const baseItem = {
          gid: variantGid || undefined,
          referenceNumber: referenceNumber || '',
          sku: partNumber || '',
          dateCode: dateCode || undefined,
          dateInstalled: dateInstalled || undefined,
          dateRemoved: dateRemoved || undefined,
          failureReason: failureReasonId || failureCode || undefined,
          failureReasonOtherText: failureReasonOtherText || undefined,
        };

        if (itemId) {
          currentItemIds.add(itemId);
          itemsToUpdate.push({
            id: itemId,
            ...baseItem,
          });
        } else {
          const createItem = {...baseItem, quantity};
          if (showReturnType) createItem.returnType = '1';
          itemsToCreate.push(createItem);
        }
      }

      const originalIds = loadedDraftData?.items?.map((i) => String(i.id ?? i.itemId ?? '').trim()).filter(Boolean) || [];
      const idsToDelete = originalIds.filter((id) => !currentItemIds.has(id));

      return {
        customerReference: customerReference || undefined,
        emailRecipients,
        itemsToDelete: idsToDelete.length ? {ids: idsToDelete} : undefined,
        itemsToCreate: itemsToCreate.length ? itemsToCreate : undefined,
        itemsToUpdate: itemsToUpdate.length ? itemsToUpdate : undefined,
      };
    }

    /**
     * Update existing warranty return via API.
     * @param {string} draftId - The warranty return gadget ID
     * @param {Object} payload - The update payload
     * @returns {Promise<{success: boolean, data?: any, error?: string}>}
     */
    async function updateWarrantyReturn(draftId, payload) {
      if (!draftId || !payload) return {success: false, error: 'Missing draftId, payload or env'};
      let token = window.myJWT;
      if (!token || typeof token !== 'string') {
        await new Promise((r) => setTimeout(r, 500));
        token = window.myJWT;
      }
      if (!token || typeof token !== 'string') return {success: false, error: 'Not authenticated'};

      try {
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns/${draftId}/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return {success: false, error: data?.message || data?.error || `Request failed (${res.status})`};
        return {success: true, data};
      } catch (e) {
        console.warn('Warranty table: update failed', e);
        return {success: false, error: e?.message || 'Unknown error'};
      }
    }

    /**
     * Submit warranty return to API.
     * @param {Object} payload - The warranty-returns payload
     * @returns {Promise<{success: boolean, data?: any, error?: string}>}
     */
    async function submitWarrantyReturn(payload) {
      if (!payload) return {success: false, error: 'Missing payload or env'};
      let token = window.myJWT;
      if (!token || typeof token !== 'string') {
        await new Promise((r) => setTimeout(r, 500));
        token = window.myJWT;
      }
      if (!token || typeof token !== 'string') return {success: false, error: 'Not authenticated'};

      try {
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return {success: false, error: data?.message || data?.error || `Request failed (${res.status})`};
        return {success: true, data};
      } catch (e) {
        console.warn('Warranty table: submit failed', e);
        return {success: false, error: e?.message || 'Unknown error'};
      }
    }

    /**
     * @param {string} value
     * @returns {boolean}
     */
    function isValidEmail(value) {
      return !!value && EMAIL_REGEX.test(value.trim());
    }

    /**
     * Collect committed and pending email recipients from the return info email chips field.
     * @param {Element|null|undefined} returnInfo
     * @returns {string[]}
     */
    function getEmailRecipients(returnInfo) {
      if (!returnInfo) return [];
      const emailVal = returnInfo.querySelector('[data-email-chips-hidden]')?.value?.trim() || '';
      const pendingEmail = returnInfo.querySelector('[data-email-chips-input]')?.value?.trim() || '';
      const emails = emailVal
        ? emailVal
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
        : [];
      const normalizedPending = pendingEmail.toLowerCase();
      if (pendingEmail && isValidEmail(pendingEmail) && !emails.includes(normalizedPending)) {
        emails.push(normalizedPending);
      }
      return emails;
    }

    /**
     * Validate rs-warranty-return-info__input elements (Customer Reference, Return Type, Email).
     * @param {Element} sectionEl - The warranty initiate return section (parent of table and return info)
     * @returns {boolean} True if valid
     */
    function validateReturnInfo(sectionEl) {
      if (!sectionEl) return true;
      const returnInfo = sectionEl.querySelector('.rs-warranty-return-info');
      if (!returnInfo) return true;

      const channelId = sectionEl.dataset?.channelId || container.dataset?.channelId;
      const showReturnType = channelId === '11' || channelId === 11;
      let isValid = true;

      const customerRefInput = returnInfo.querySelector('input[name="return[customer_reference]"]');
      const customerRefWrapper = customerRefInput?.closest('.rs-input__content');
      const customerRefVal = customerRefInput?.value?.trim() || '';
      if (!customerRefVal) {
        if (customerRefWrapper) {
          customerRefWrapper.classList.add('rs-error');
          const msgSpan = customerRefWrapper.querySelector('.rs-error__message span');
          if (msgSpan) msgSpan.textContent = 'Customer Reference or PO Number is required';
        }
        isValid = false;
      } else if (customerRefWrapper) {
        customerRefWrapper.classList.remove('rs-error');
      }

      if (showReturnType) {
        const returnTypeWrapper = returnInfo.querySelector('.rs-custom-select__wrapper');
        const returnTypeHidden = returnInfo.querySelector('input[name*="return_type"]');
        const returnTypeVal = returnTypeHidden?.value?.trim() || '';
        if (!returnTypeVal) {
          if (returnTypeWrapper) {
            returnTypeWrapper.classList.add('rs-error');
            const msgSpan = returnTypeWrapper.querySelector('.rs-error__message span');
            if (msgSpan) msgSpan.textContent = 'Return Type is required';
          }
          isValid = false;
        } else if (returnTypeWrapper) {
          returnTypeWrapper.classList.remove('rs-error');
        }
      }

      const emailChips = returnInfo.querySelector('[data-email-chips]');
      const emailInput = returnInfo.querySelector('[data-email-chips-input]');
      const emailHidden = returnInfo.querySelector('[data-email-chips-hidden]');
      const emailErrorEl = returnInfo.querySelector('[data-email-chips-error]');
      const emailErrorTextEl = returnInfo.querySelector('[data-email-chips-error-text]');
      const pendingEmail = emailInput?.value?.trim() || '';
      const committedEmails = (emailHidden?.value || '')
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean);
      const invalidEmail = (pendingEmail && !isValidEmail(pendingEmail)) || committedEmails.find((e) => !isValidEmail(e));

      if (invalidEmail) {
        if (emailChips) emailChips.classList.add('rs-error');
        if (emailErrorEl) emailErrorEl.hidden = false;
        if (emailErrorTextEl) emailErrorTextEl.textContent = 'Please enter a valid email address.';
        isValid = false;
      } else if (emailChips) {
        emailChips.classList.remove('rs-error');
        if (emailErrorEl) emailErrorEl.hidden = true;
      }

      return isValid;
    }

    /**
     * Clear rs-error from all fields in the table (e.g. before re-validating).
     * @param {Element} tableContainer - The warranty table container
     */
    function clearValidationErrors(tableContainer) {
      tableContainer.querySelectorAll('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error').forEach((el) => el.classList.remove('rs-error'));
      tableContainer.querySelectorAll('.rs-warranty-table__row--error').forEach((row) => row.classList.remove('rs-warranty-table__row--error'));
      const section = tableContainer.closest('.rs-warranty-initiate-return');
      if (section) {
        section.querySelectorAll('.rs-warranty-return-info .rs-input__content.rs-error, .rs-warranty-return-info .rs-custom-select__wrapper.rs-error, .rs-warranty-return-info [data-email-chips].rs-error').forEach((el) => el.classList.remove('rs-error'));
        section.querySelectorAll('.rs-warranty-return-info [data-email-chips-error]').forEach((el) => {
          el.hidden = true;
        });
      }
    }

    function updateSubmitButtonState() {
      const submitBtn = container.querySelector(selectorSubmitBtn);
      if (!submitBtn) return;
      const section = container.closest('.rs-warranty-initiate-return');
      const hasErrors = container.querySelector('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error') || section?.querySelector('.rs-warranty-return-info .rs-input__content.rs-error, .rs-warranty-return-info .rs-custom-select__wrapper.rs-error, .rs-warranty-return-info [data-email-chips].rs-error');
      submitBtn.disabled = !!hasErrors;
    }

    function updateSaveButtonState() {
      const saveBtn = container.querySelector(selectorSaveBtn);
      if (!saveBtn) return;
      const section = container.closest('.rs-warranty-initiate-return');
      const hasErrors = container.querySelector('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error') || section?.querySelector('.rs-warranty-return-info .rs-input__content.rs-error, .rs-warranty-return-info .rs-custom-select__wrapper.rs-error, .rs-warranty-return-info [data-email-chips].rs-error');
      saveBtn.disabled = !!hasErrors;
    }

    /**
     * Full validation used before save/submit and after bulk file upload: clears prior errors,
     * validates table rows, return header, and SKU visibility/returnability.
     * @returns {Promise<boolean>}
     */
    async function runWarrantyFormValidation() {
      clearValidationErrors(container);
      const section = container.closest('.rs-warranty-initiate-return');
      const tableValid = validateRows(container);
      const returnInfoValid = validateReturnInfo(section);
      const skuValid = await validateSkus(container);
      return tableValid && returnInfoValid && skuValid;
    }

    /**
     * Wire up save and submit buttons to validate rows and submit to warranty-returns API.
     * Submit button opens confirmation modal; API is called only when modal Submit is clicked.
     */
    function initSaveSubmitValidation() {
      const form = container.querySelector('form');
      const saveBtn = container.querySelector(selectorSaveBtn);
      const submitBtn = container.querySelector(selectorSubmitBtn);
      const modal = container.querySelector('[data-warranty-submit-modal]');
      const modalSubmitBtn = modal?.querySelector('.rs-warranty-submit-modal__submit');
      const modalCancelBtn = modal?.querySelector('.rs-warranty-submit-modal__cancel');
      const modalCloseButtons = modal ? modal.querySelectorAll('.rs-warranty-submit-modal__close, .rs-modal__close-button') : [];

      let pendingSubmitPayload = null;

      async function runValidation() {
        return runWarrantyFormValidation();
      }

      function openSubmitModal() {
        if (!modal) return;
        modal.classList.remove('rs-hidden');
        modal.setAttribute('aria-hidden', 'false');
      }

      function closeSubmitModal() {
        if (!modal) return;
        modal.classList.add('rs-hidden');
        modal.setAttribute('aria-hidden', 'true');
        pendingSubmitPayload = null;
      }

      async function handleSaveOrSubmit(draft) {
        if (!(await runValidation())) {
          if (submitBtn) submitBtn.disabled = true;
          if (saveBtn) saveBtn.disabled = true;
          return;
        }
        const draftId = new URLSearchParams(window.location.search).get('draftId') || loadedDraftData?.draftId;
        if (draft && draftId) {
          const payload = await buildWarrantyReturnsUpdatePayload(container);
          if (!payload) {
            runValidation();
            return;
          }
          const result = await updateWarrantyReturn(draftId, payload);
          if (result.success) {
            document.cookie = 'warranty_return_submitted=true; path=/';
            window.location.href = '/pages/saved-warranty-returns';
          } else {
            alert('Update failed: ' + (result.error || 'Unknown error'));
          }
          return;
        }
        if (!document.body.dataset.soldTo || !document.body.dataset.shipTo) {
          alert(container.dataset.noCompanyError || 'Your account is not linked to a company or location, so this return cannot be saved. Please contact support.');
          return;
        }
        const payload = await buildWarrantyReturnsPayload(container, draft);
        if (!payload) {
          runValidation();
          return;
        }
        if (draft) {
          const result = await submitWarrantyReturn(payload);
          if (result.success) {
            document.cookie = 'warranty_return_submitted=true; path=/';
            window.location.href = '/pages/saved-warranty-returns';
          } else {
            alert('Submission failed: ' + (result.error || 'Unknown error'));
          }
        } else {
          // Check if the return was a draft and is now being submitted
          const isDraft = window.location.search.includes('draftId');
          if (isDraft) {
            const result = await submitDraftWarrantyReturn();
            if (result.success) {
              // Set a cookie to indicate that the user has submitted a warranty return
              document.cookie = 'warranty_return_submitted=true; path=/';

              // Route the user to /warranty-returns page
              window.location.href = '/pages/warranty-returns';
            } else {
              alert('Submission failed: ' + (result.error || 'Unknown error'));
            }
          } else {
            pendingSubmitPayload = payload;
            openSubmitModal();
          }
        }
      }

      async function submitDraftWarrantyReturn() {
        const draftId = new URLSearchParams(window.location.search).get('draftId') || loadedDraftData?.draftId;
        if (!draftId) return {success: false, error: 'Draft ID not found'};
        const token = window.myJWT;
        const ctx = getContextFromCookie();
        const res = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({
            gadgetId: draftId,
            salesArea: (() => {
              // sales_area can be an array or a single object, with or without `fields`
              const sa = ctx?.sales_area;
              const raw = Array.isArray(sa) ? sa[0]?.fields || sa[0] : sa?.fields || sa || {};
              return raw?.external_id || sa?.external_id || document.body?.dataset?.currentSalesArea || '';
            })(),
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) return {success: false, error: data?.message || data?.error || `Request failed (${res.status})`};
        return {success: true, data};
      }

      async function handleModalSubmit() {
        if (!pendingSubmitPayload) return;
        const originalText = modalSubmitBtn?.textContent;
        if (modalSubmitBtn) modalSubmitBtn.disabled = true;
        if (modalSubmitBtn) modalSubmitBtn.textContent = 'Submitting...';
        const result = await submitWarrantyReturn(pendingSubmitPayload);
        if (modalSubmitBtn) {
          modalSubmitBtn.disabled = false;
          modalSubmitBtn.textContent = originalText || 'Submit';
        }
        if (result.success) {
          closeSubmitModal();
          // Set a cookie to indicate that the user has submitted a warranty return
          document.cookie = 'warranty_return_submitted=true; path=/';

          // Route the user to /warranty-returns page
          window.location.href = '/pages/warranty-returns';
        } else {
          alert('Submission failed: ' + (result.error || 'Unknown error'));
        }
      }

      if (saveBtn) {
        saveBtn.addEventListener('click', () => handleSaveOrSubmit(true));
      }

      if (form && submitBtn) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          handleSaveOrSubmit(false);
        });
      }

      if (modal) {
        modalCloseButtons.forEach((btn) => {
          btn.addEventListener('click', closeSubmitModal);
        });
        if (modalCancelBtn) {
          modalCancelBtn.addEventListener('click', closeSubmitModal);
        }
        if (modalSubmitBtn) {
          modalSubmitBtn.addEventListener('click', handleModalSubmit);
        }
      }

      container.addEventListener('input', (e) => {
        const wrapper = e.target.closest?.('.rs-input__content, .rs-custom-select__wrapper');
        if (wrapper && wrapper.closest?.(selectorTableRow)) {
          wrapper.classList.remove('rs-error');
          const row = wrapper.closest(selectorTableRow);
          if (row && !row.querySelector('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error')) {
            row.classList.remove('rs-warranty-table__row--error');
          }
        }
        if (wrapper?.closest?.('.rs-warranty-return-info')) {
          wrapper.classList.remove('rs-error');
        }
        updateSubmitButtonState();
        updateSaveButtonState();
      });
      container.addEventListener('change', (e) => {
        const wrapper = e.target.closest?.('.rs-input__content, .rs-custom-select__wrapper');
        if (wrapper && wrapper.closest?.(selectorTableRow)) {
          wrapper.classList.remove('rs-error');
          const row = wrapper.closest(selectorTableRow);
          if (row && !row.querySelector('.rs-input__content.rs-error, .rs-custom-select__wrapper.rs-error')) {
            row.classList.remove('rs-warranty-table__row--error');
          }
        }
        if (wrapper?.closest?.('.rs-warranty-return-info')) {
          wrapper.classList.remove('rs-error');
        }
        updateSubmitButtonState();
        updateSaveButtonState();
      });

      const section = container.closest('.rs-warranty-initiate-return');
      if (section) {
        section.addEventListener('input', (e) => {
          const wrapper = e.target.closest?.('.rs-input__content, .rs-custom-select__wrapper, [data-email-chips]');
          if (wrapper?.closest?.('.rs-warranty-return-info')) {
            wrapper.classList.remove('rs-error');
            const errorEl = wrapper.querySelector?.('[data-email-chips-error]');
            if (errorEl) errorEl.hidden = true;
          }
          updateSubmitButtonState();
          updateSaveButtonState();
        });
        section.addEventListener('change', (e) => {
          const wrapper = e.target.closest?.('.rs-input__content, .rs-custom-select__wrapper');
          if (wrapper?.closest?.('.rs-warranty-return-info')) wrapper.classList.remove('rs-error');
          updateSubmitButtonState();
          updateSaveButtonState();
        });
      }
    }

    /**
     * Clone template into a cleared, re-inittable row (not yet appended).
     * @returns {Element|null}
     */
    function prepareBlankRowFromTemplate() {
      if (!template) return null;
      const newRow = template.cloneNode(true);
      newRow.classList.add('rs-warranty-table__row--clone');
      fixClonedIds(newRow);
      clearRow(newRow);
      resetClonedDateInputs(newRow);
      reinitCustomSelects(newRow);
      return newRow;
    }

    /**
     * Append one blank row when under max row count.
     * @returns {boolean}
     */
    function addSingleBlankRow() {
      if (!tableBody || !template) return false;
      if (tableBody.querySelectorAll(selectorTableRow).length >= maxRows) return false;
      const newRow = prepareBlankRowFromTemplate();
      if (!newRow) return false;
      tableBody.appendChild(newRow);
      initDateInputsInContainer(tableBody);
      initCustomerPartNumberSuggestionsInRow(newRow);
      updateAddMoreButton();
      return true;
    }

    let autoAddBlankRowTimer = null;

    /**
     * When the last row has every required field and valid dates, append one blank row (if under max).
     */
    function maybeAutoAddBlankRow() {
      if (!tableBody || !template) return;
      if (container.dataset.populatingFromDraft || container.dataset.populatingBulk) return;
      const rows = tableBody.querySelectorAll(selectorTableRow);
      if (!rows.length) return;
      const lastRow = rows[rows.length - 1];
      if (!isRowCompleteForAutoAdd(lastRow)) return;
      if (rows.length >= maxRows) return;
      addSingleBlankRow();
    }

    function scheduleMaybeAutoAddBlankRow() {
      if (autoAddBlankRowTimer) clearTimeout(autoAddBlankRowTimer);
      autoAddBlankRowTimer = setTimeout(() => {
        autoAddBlankRowTimer = null;
        maybeAutoAddBlankRow();
      }, 100);
    }

    function initAutoAddBlankRowWhenLastComplete() {
      if (!tableBody || !template) return;
      function onRowFieldInteraction() {
        scheduleMaybeAutoAddBlankRow();
      }
      container.addEventListener('input', onRowFieldInteraction);
      container.addEventListener('change', onRowFieldInteraction);
    }

    /**
     * Handle the Add More button: clone template rows, reset them, append to table.
     */
    function initAddMore() {
      if (!addMoreBtn || !tableBody || !template) return;

      addMoreBtn.addEventListener('click', () => {
        const rows = tableBody.querySelectorAll(selectorTableRow);
        const toAdd = rows.length ? Math.min(addMoreCount, Math.max(0, maxRows - rows.length)) : Math.min(addMoreCount, maxRows);

        const frag = document.createDocumentFragment();
        let count = rows.length;
        for (let i = 0; i < toAdd && count < maxRows; i++) {
          const newRow = prepareBlankRowFromTemplate();
          if (!newRow) break;
          frag.appendChild(newRow);
          count++;
        }
        tableBody.appendChild(frag);
        updateMasterCheckboxState();
        initDateInputsInContainer(tableBody);
        initCustomerPartNumberSuggestionsInContainer(frag);
        updateAddMoreButton();
      });
    }

    initMasterCheckbox();
    initRowCheckboxes();
    initWarrantyMultipleSelection();
    initRowActions();
    initFailureOtherInputs();
    initFailureSelectDropdownFloat();
    initDateCodeInput();
    initSkuFailureFetch();
    initCustomerPartNumberSuggestionsInContainer();
    initSaveSubmitValidation();
    initAddMore();
    initAutoAddBlankRowWhenLastComplete();
    observeNewRows();
    updateAddMoreButton();

    /**
     * Initialize file upload handler for warranty return: when a file is added to
     * .rs-warranty-initiate-return .rs-upload-file, parse and populate the table.
     * CSV columns: Reference_Number, Product_ID, Quantity, Date_Code, Date_Installed,
     * Date_Removed, Reason_For_Failure, Other_Reason. Product_ID maps to SKU.
     */
    function initWarrantyUploadFileHandler() {
      const section = container.closest('.rs-warranty-initiate-return');
      if (!section) return;

      const uploadBlock = section.querySelector('.rs-upload-file');
      const uploadInput = uploadBlock?.querySelector('input[type="file"]');

      const WARRANTY_CSV_HEADERS = ['Reference_Number', 'Product_ID', 'Quantity', 'Date_Code', 'Date_Installed', 'Date_Removed', 'Reason_For_Failure', 'Other_Reason'];
      const PRODUCT_ID_ALIASES = [
        'Product_ID',
        'SKU',
        'Customer_Part_Number',
        'Customer Part Number',
        'SKU / Customer Part Number',
        'SKU/Customer Part Number',
      ];
      const INSTR_LOWER = 'instructions:';

      function stripInstructionLine(text) {
        if (!text || typeof text !== 'string') return text;
        if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
        const nl = text.indexOf('\n');
        const first = stripWrappingQuotes(nl === -1 ? text : text.slice(0, nl));
        if (first.toLowerCase().startsWith(INSTR_LOWER)) {
          return nl === -1 ? '' : text.slice(nl + 1);
        }
        return text;
      }

      function parseCsvToWarrantyData(text) {
        const cleaned = stripInstructionLine(text);
        if (typeof window.Papa === 'undefined') return {error: 'no-papa'};
        let result = null;
        window.Papa.parse(cleaned, {
          header: true,
          skipEmptyLines: true,
          complete: (r) => {
            result = r;
          },
        });
        if (!result?.data?.length) return {error: 'incomplete'};
        const headers = result.meta?.fields || [];
        const lower = headers.map((h) => stripWrappingQuotes(h).toLowerCase());
        const hasProductId = PRODUCT_ID_ALIASES.some((a) => lower.includes(a.toLowerCase()));
        if (!hasProductId) return {error: 'mismatch'};
        const idx = {};
        WARRANTY_CSV_HEADERS.forEach((h) => {
          const i = headers.findIndex((x) => stripWrappingQuotes(x).toLowerCase() === h.toLowerCase());
          if (i >= 0) idx[h] = headers[i];
        });
        const productIdKey = headers.find((x) => PRODUCT_ID_ALIASES.some((a) => a.toLowerCase() === stripWrappingQuotes(x).toLowerCase()));
        const getRowVal = (row, key) => {
          const header = idx[key] ?? (key === 'Product_ID' ? productIdKey : null);
          return header ? stripWrappingQuotes(row[header] ?? '') : '';
        };
        const data = result.data
          .filter((row) => getRowVal(row, 'Product_ID') !== '')
          .map((row) => ({
            reference_number: getRowVal(row, 'Reference_Number'),
            sku: getRowVal(row, 'Product_ID'),
            quantity: getRowVal(row, 'Quantity') || '1',
            date_code: getRowVal(row, 'Date_Code'),
            date_installed: normalizeDateForInput(getRowVal(row, 'Date_Installed')),
            date_removed: normalizeDateForInput(getRowVal(row, 'Date_Removed')),
            reason_for_failure: getRowVal(row, 'Reason_For_Failure'),
            other_reason: getRowVal(row, 'Other_Reason'),
          }));
        return {data};
      }

      function parseXlsxToWarrantyData(arrayBuffer) {
        if (typeof window.XLSX === 'undefined') return {error: 'no-xlsx'};
        const wb = window.XLSX.read(arrayBuffer, {type: 'array'});
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = window.XLSX.utils.sheet_to_json(sheet, {header: 1, raw: false, blankrows: false});
        while (rows.length && rows[0].every((c) => stripWrappingQuotes(c) === '')) rows.shift();
        if (rows.length) {
          const cell0 = stripWrappingQuotes(rows[0][0]).toLowerCase();
          if (cell0.startsWith(INSTR_LOWER)) rows.shift();
        }
        const headerIdx = rows.findIndex((r) => {
          const lower = r.map((c) => stripWrappingQuotes(c).toLowerCase());
          return PRODUCT_ID_ALIASES.some((a) => lower.includes(a.toLowerCase()));
        });
        if (headerIdx === -1) return {error: 'mismatch'};
        const headers = rows[headerIdx].map((h) => stripWrappingQuotes(h));
        const getIdx = (name) => {
          if (name === 'Product_ID') {
            const i = headers.findIndex((h) => PRODUCT_ID_ALIASES.some((a) => a.toLowerCase() === (h || '').toLowerCase()));
            return i >= 0 ? i : headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());
          }
          return headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());
        };
        const dataRows = rows.slice(headerIdx + 1).filter((r) => r.some((c) => stripWrappingQuotes(c) !== ''));
        const data = dataRows
          .filter((r) => stripWrappingQuotes(r[getIdx('Product_ID')] ?? '') !== '')
          .map((r) => {
            const get = (name) => stripWrappingQuotes(r[getIdx(name)] ?? '');
            return {
              reference_number: get('Reference_Number'),
              sku: get('Product_ID'),
              quantity: get('Quantity') || '1',
              date_code: get('Date_Code'),
              date_installed: normalizeDateForInput(get('Date_Installed')),
              date_removed: normalizeDateForInput(get('Date_Removed')),
              reason_for_failure: get('Reason_For_Failure'),
              other_reason: get('Other_Reason'),
            };
          });
        return {data};
      }

      function normalizeDateForInput(val) {
        if (!val) return '';
        const s = String(val).trim();
        let y, mo, day;
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          const parts = s.slice(0, 10).split('-');
          y = parts[0];
          mo = parts[1];
          day = parts[2];
        } else {
          const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
          if (m) {
            y = m[3].length === 2 ? '20' + m[3] : m[3];
            mo = m[1].padStart(2, '0');
            day = m[2].padStart(2, '0');
          } else {
            const d = new Date(s);
            if (!isNaN(d.getTime())) {
              y = String(d.getFullYear());
              mo = String(d.getMonth() + 1).padStart(2, '0');
              day = String(d.getDate()).padStart(2, '0');
            } else {
              return s;
            }
          }
        }
        return `${mo}/${day}/${y}`;
      }

      const FAILURE_VALUE_MAP = {
        defective: 'defective',
        damaged: 'damaged',
        'wrong item': 'wrong_item',
        wrong_item: 'wrong_item',
        'no longer needed': 'no_longer_needed',
        no_longer_needed: 'no_longer_needed',
        other: 'other',
      };

      function selectFailureReason(row, reasonText, otherText, gadgetId) {
        const failureCell = row.querySelector('.rs-warranty-table__cell--failure');
        if (!failureCell) return;
        const wrapper = failureCell.querySelector('.rs-custom-select__wrapper');
        const hiddenInput = wrapper?.querySelector('input[name*="failure_reason"]:not([name*="other"])');
        const valueSpan = wrapper?.querySelector('.rs-custom-select__value');
        const options = Array.from(wrapper?.querySelectorAll('.rs-custom-select__option') || []);
        const otherInput = failureCell.querySelector('.rs-warranty-table__failure-other-input, input[name*="failure_reason_other"]');
        const reason = (reasonText || '').trim().toLowerCase();
        const gid = (gadgetId || '').trim();

        const getOptVal = (o) => (o.dataset.value || o.getAttribute('data-value') || '').trim();
        const getOptCode = (o) => (o.dataset.code || o.getAttribute('data-code') || '').toLowerCase();
        const getOptLabel = (o) => (o.getAttribute('aria-label') || o.querySelector('.rs-custom-select__option-label')?.textContent || '').toLowerCase().trim();

        const opt = options.find((o) => gid && getOptVal(o) === gid) || options.find((o) => getOptVal(o).toLowerCase() === (FAILURE_VALUE_MAP[reason] || '')) || options.find((o) => reason && getOptCode(o) === reason) || options.find((o) => getOptLabel(o) === reason) || (reason ? options.find((o) => getOptLabel(o).includes(reason)) : null);

        if (opt) {
          const targetValue = getOptVal(opt);
          options.forEach((o) => {
            o.classList.toggle('is-selected', o === opt);
            o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
          });
          if (valueSpan) {
            valueSpan.textContent = opt.querySelector('.rs-custom-select__option-label')?.textContent || targetValue;
            valueSpan.classList.remove('rs-custom-select__value--placeholder');
          }
          if (hiddenInput) {
            hiddenInput.value = targetValue;
          }
        }
        if (otherInput && otherText) {
          otherInput.value = otherText;
          otherInput.dispatchEvent(new Event('input', {bubbles: true}));
          otherInput.dispatchEvent(new Event('change', {bubbles: true}));
        }
        syncFailureOtherInput(failureCell);
      }

      async function populateWarrantyTableFromData(data) {
        container.dataset.populatingBulk = 'true';
        try {
          const rows = tableBody.querySelectorAll(selectorTableRow);
          const needed = Math.min(data.length, maxRows);
          if (rows.length < needed && template) {
            const frag = document.createDocumentFragment();
            for (let i = rows.length; i < needed; i++) {
              const newRow = template.cloneNode(true);
              newRow.classList.add('rs-warranty-table__row--clone');
              fixClonedIds(newRow);
              clearRow(newRow);
              resetClonedDateInputs(newRow);
              reinitCustomSelects(newRow);
              frag.appendChild(newRow);
            }
            tableBody.appendChild(frag);
            initDateInputsInContainer(tableBody);
          }
          const allRows = tableBody.querySelectorAll(selectorTableRow);
          const rowDataSlice = data.slice(0, needed);

          const uniqueSkus = [...new Set(rowDataSlice.map((d) => normalizeSkuOrCustomerPartNumber(d.sku)).filter(Boolean))];
          const skuToGid = {};
          if (uniqueSkus.length) {
            const gidResults = await Promise.all(uniqueSkus.map((sku) => fetchVariantGidBySku(sku)));
            uniqueSkus.forEach((sku, i) => {
              if (gidResults[i]) skuToGid[sku] = gidResults[i];
            });
          }
          const variantGids = Object.values(skuToGid);
          const failureData = variantGids.length ? await fetchFailureReasons(variantGids) : {};

          for (let i = 0; i < rowDataSlice.length; i++) {
            const rowData = rowDataSlice[i];
            const row = allRows[i];
            if (!row) continue;
            const setInput = (sel, val) => {
              const inp = row.querySelector(sel);
              if (inp && val !== undefined && val !== null) {
                inp.value = String(val);
                inp.dispatchEvent(new Event('input', {bubbles: true}));
                inp.dispatchEvent(new Event('change', {bubbles: true}));
              }
            };
            const setDateInput = (cellClass, val) => {
              const cell = row.querySelector(`.${cellClass}`);
              if (!cell || val === undefined || val === null || String(val).trim() === '') return;
              const valStr = String(val).trim();
              const inp = cell.querySelector('input[name*="date_installed"]') || cell.querySelector('input[name*="date_removed"]') || Array.from(cell.querySelectorAll('input')).find((i) => i._flatpickr);
              if (!inp) return;
              if (inp._flatpickr) {
                inp._flatpickr.setDate(valStr, true);
              } else {
                inp.value = valStr;
                inp.dispatchEvent(new Event('input', {bubbles: true}));
                inp.dispatchEvent(new Event('change', {bubbles: true}));
              }
            };
            setInput('input[name*="reference_number"]', rowData.reference_number);
            setInput('input[name*="sku"]', normalizeSkuOrCustomerPartNumber(rowData.sku));
            setInput('input[name*="date_code"]', rowData.date_code);
            setDateInput('rs-warranty-table__cell--date-installed', rowData.date_installed);
            setDateInput('rs-warranty-table__cell--date-removed', rowData.date_removed);
            setInput('input[name*="quantity"]', rowData.quantity);

            const sku = normalizeSkuOrCustomerPartNumber(rowData.sku);
            const variantGid = sku ? skuToGid[sku] : null;
            const failureReasons = variantGid ? failureData[variantGid]?.failureReasons : null;
            if (failureReasons?.length) {
              populateFailureSelectWithApiOptions(row, failureReasons);
            }
            selectFailureReason(row, rowData.reason_for_failure, rowData.other_reason, rowData.failure_gadget_id);
            if (rowData.item_id) row.setAttribute('data-item-id', rowData.item_id);
            const returnValueEl = row.querySelector('.rs-warranty-return__value');
            if (returnValueEl && rowData.returnType) {
              returnValueEl.textContent = String(rowData.returnType);
            }
          }
          updateAddMoreButton();
          // Rows the upload appended are (re)initialized on a deferred task, and each filled
          // field queues its suggestion lookup there. Yield once so that task runs, then wait
          // for the lookups to render: validateSkus resolves a row's SKU from the rendered
          // suggestion, so it would otherwise run against an empty list.
          await new Promise((resolve) => setTimeout(resolve, 0));
          await whenCustomerPartNumberSuggestionsSettle();
          await runWarrantyFormValidation();
          updateSubmitButtonState();
          updateSaveButtonState();
        } finally {
          delete container.dataset.populatingBulk;
        }
      }

      if (uploadInput) {
        uploadInput.addEventListener(
          'change',
          (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const isCsv = /\.csv$/i.test(file.name) || (file.type || '').toLowerCase().includes('csv');
            const isXlsx = /\.xlsx?$/i.test(file.name) || (file.type || '').toLowerCase().includes('spreadsheet');
            if (!isCsv && !isXlsx) return;

            e.stopImmediatePropagation();
            e.target.value = '';

            const onResult = (result) => {
              if (result.error) return;
              if (!result.data?.length) return;
              populateWarrantyTableFromData(result.data);
            };

            if (isCsv) {
              const reader = new FileReader();
              reader.onload = (ev) => {
                const result = parseCsvToWarrantyData(ev.target?.result || '');
                onResult(result);
              };
              reader.readAsText(file);
            } else {
              const reader = new FileReader();
              reader.onload = (ev) => {
                const buf = ev.target?.result;
                const result = parseXlsxToWarrantyData(buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(0));
                onResult(result);
              };
              reader.readAsArrayBuffer(file);
            }
          },
          true,
        );
      }

      async function loadDraftFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const draftId = params.get('draftId');
        if (!draftId) return;

        const waitForJWT = (maxMs = 5000) =>
          new Promise((resolve) => {
            const check = () => {
              if (window.myJWT) return resolve(true);
              if (maxMs <= 0) return resolve(false);
              maxMs -= 100;
              setTimeout(check, 100);
            };
            check();
          });

        if (!(await waitForJWT())) return;

        const tryFetch = async () => {
          if (!window.myJWT) return null;
          const res = await fetch(`${getCustomerAppBaseUrl()}/api/warranty-returns/${encodeURIComponent(draftId)}`, {
            method: 'GET',
            headers: {Authorization: `Bearer ${window.myJWT}`},
          });
          if (!res.ok) return null;
          return res.json();
        };

        const data = await tryFetch();
        if (!data) return;

        const items = data.items ?? data.rows ?? [];
        if (!items.length) return;

        initDateInputsInContainer(tableBody);

        loadedDraftData = {draftId, items};

        const tableData = items.map((item) => {
          const ref = item.referenceNumber ?? item.reference_number ?? '';
          const sku = normalizeSkuOrCustomerPartNumber(item.materialNumber ?? item.partNumber ?? item.part_number ?? item.sku ?? '');
          const qty = item.quantity ?? 1;
          const dateCode = item.dateCode ?? item.date_code ?? '';
          const dateInst = item.dateInstalled ?? item.date_installed ?? '';
          const dateRem = item.dateRemoved ?? item.date_removed ?? '';
          const failure = item.failureReason;
          const reasonText = failure?.reason ?? failure?.code ?? item.failureReasonCode ?? '';
          const otherText = item.failureReasonOtherText ?? item.failure_reason_other ?? '';
          const failureGadgetId = failure?.gadgetId ?? failure?.id ?? item.failureReasonId ?? '';
          const itemId = String(item.id ?? item.itemId ?? '').trim();
          // Prefer title/type for display (matches .rs-warranty-return__value / payload matching)
          const returnType = item.returnType?.type || item.returnType?.title || item.returnType?.code || '';
          return {
            reference_number: String(ref),
            sku: String(sku),
            quantity: String(qty),
            date_code: String(dateCode),
            date_installed: normalizeDateForInput(String(dateInst)),
            date_removed: normalizeDateForInput(String(dateRem)),
            reason_for_failure: String(reasonText),
            other_reason: String(otherText),
            failure_gadget_id: String(failureGadgetId),
            item_id: itemId,
            returnType: returnType,
          };
        });

        container.dataset.populatingFromDraft = 'true';
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            populateWarrantyTableFromData(tableData);
            setTimeout(() => delete container.dataset.populatingFromDraft, 3000);
          });
        });

        const returnInfo = section?.querySelector('.rs-warranty-return-info');
        if (returnInfo) {
          const customerRef = data.customerReference ?? data.customer_reference ?? '';
          const customerRefInput = returnInfo.querySelector('input[name*="customer_reference"]');
          if (customerRefInput && customerRef) customerRefInput.value = String(customerRef);

          const emailRecipients = data.emailRecipients ?? data.email_recipients ?? data.emailAddresses ?? [];
          if (emailRecipients.length) {
            const emailHidden = returnInfo.querySelector('[data-email-chips-hidden]');
            if (emailHidden) emailHidden.value = emailRecipients.join(', ');
            const emailChips = returnInfo.querySelector('[data-email-chips]');
            if (emailChips && typeof window.initEmailChips === 'function') {
              // Prepopulate the email chips with the email recipients
              const prepopulate = true;
              window.initEmailChips(emailChips, prepopulate);
            }
          }

          await returnTypeSelectReady;
          const returnTypeTitle = data.returnType?.title || data.returnType?.type || '';
          if (returnTypeTitle) {
            const returnTypeWrapper = returnInfo.querySelector('.rs-custom-select__wrapper.rs-warranty-return-info__input');
            const options = returnTypeWrapper?.querySelectorAll('.rs-custom-select__option') || [];
            const match = Array.from(options).find((opt) => {
              const label = opt.getAttribute('aria-label') || opt.querySelector('.rs-custom-select__option-label')?.textContent || '';
              return label.trim().toLowerCase() === String(returnTypeTitle).trim().toLowerCase();
            });
            match?.click();
          }
        }
      }

      loadDraftFromUrl();
    }
    const returnTypeSelectReady = initReturnTypeSelect();

    // Initialize quantity behavior.
    initQuantityInputsForReturnType();

    // Recheck after the API populates the dropdown.
    returnTypeSelectReady.then(() => {
      updateQuantityInputsForReturnType();
    });

    // Rows added later are cloned from a template whose quantity input is disabled, so re-apply the rule.
    if (tableBody && typeof MutationObserver !== 'undefined') {
      new MutationObserver(() => updateQuantityInputsForReturnType()).observe(tableBody, {childList: true});
    }

    initWarrantyUploadFileHandler();
  }

  /**
   * Find all warranty tables on the page and initialize each one.
   */
  function initAll() {
    document.querySelectorAll(selectorWarrantyTable).forEach(init);
  }

  // Run on DOM ready, or immediately if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
